import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Clock,
  Settings,
  Save,
  Plus,
  Edit3,
  Trash2,
  X,
  FileText,
  Package,
  Upload,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Mail,
  MessageSquare,
  ArrowUp,
  ArrowDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Hash,
  Receipt,
  Bell,
  Building,
  Search,
  Filter
} from 'lucide-react';
import axios from 'axios';

const ManageBoardingCalendar = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('manage-spaces');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Manage Spaces States
  const [spaces, setSpaces] = useState([]);
  const [spaceSchedules, setSpaceSchedules] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [showSpaceModal, setShowSpaceModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingDateEntry, setEditingDateEntry] = useState(null);
  
  // Calendar View States
  const [reservations, setReservations] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [calendarView, setCalendarView] = useState('list'); // 'list' or 'calendar'
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedServiceOptions, setSelectedServiceOptions] = useState({});
  const [modalDataReady, setModalDataReady] = useState(false);
  const [modalServices, setModalServices] = useState([]);
  const [modalSpaces, setModalSpaces] = useState([]);
  const [filteredSpaces, setFilteredSpaces] = useState([]);
  const [showDeleteAllBookingsModal, setShowDeleteAllBookingsModal] = useState(false);
  const [showDeleteAllToursModal, setShowDeleteAllToursModal] = useState(false);
  const [openAccordions, setOpenAccordions] = useState({
    slotBookings: true,
    appointmentCounter: false,
    payments: false,
    notifications: false,
    taxSettings: false
  });

  const toggleAccordion = (section) => {
    setOpenAccordions(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Space Categories States
  const [spaceCategories, setSpaceCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ category_name: '', description: '' });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateForm, setDateForm] = useState({
    is_24_hours: false,
    start_time: '09:00',
    end_time: '17:00',
    slot_duration_minutes: 30,
    enable_slot_booking: false
  });
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [tempBlockedDates, setTempBlockedDates] = useState([]);
  const [spaceForm, setSpaceForm] = useState({
    space_name: '',
    description: '',
    category_id: '',
    schedule_duration_months: 1,
    is_24_hours: false,
    start_time: '09:00',
    end_time: '17:00',
    slot_duration_minutes: 30,
    enable_slot_booking: false,
    bulk_start_date: '',
    bulk_end_date: '',
    component_display_enabled: false
  });
  const [scheduleForm, setScheduleForm] = useState({
    start_time: '09:00',
    end_time: '17:00',
    slot_duration_minutes: 30,
    is_enabled: true,
    breaks: []
  });

  // Configure Form States
  const [formFields, setFormFields] = useState([]);
  const [roomFields, setRoomFields] = useState([]);
  const [specialInstructionFields, setSpecialInstructionFields] = useState([]);
  const [tourFields, setTourFields] = useState([]);
  const [selectedSection, setSelectedSection] = useState('form');
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    field_name: '',
    field_type: 'text',
    required: false,
    placeholder: '',
    description: '',
    options: ''
  });

  // Section Enable/Disable States
  const [roomDetailsEnabled, setRoomDetailsEnabled] = useState(true);
  const [specialInstructionsEnabled, setSpecialInstructionsEnabled] = useState(true);

  // Configure Services States
  const [servicesEnabled, setServicesEnabled] = useState(true);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryServices, setCategoryServices] = useState({});
  const [serviceCategoryMappings, setServiceCategoryMappings] = useState({}); // { serviceId: [categoryId1, categoryId2] }

  // Document Fields States
  const [documentFields, setDocumentFields] = useState([]);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    description: ''
  });

  // Settings States
  const [settings, setSettings] = useState({
    slot_duration: 15,
    start_time: '09:00',
    end_time: '17:00',
    dropoff_start_time: '07:00',
    dropoff_end_time: '19:00',
    pickup_start_time: '07:00',
    pickup_end_time: '19:00',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    services_enabled: true,
    payment_enabled: false,
    payment_mode: '',
    token_amount: 0,
    email_enabled: false,
    sms_enabled: false,
    document_upload_enabled: false,
    boarding_number_prefix: 'SPACE',
    boarding_number_start: 1000
  });

  // Payment Gateway States
  const [availablePaymentModes, setAvailablePaymentModes] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const primaryColor = 'var(--theme-primary)';

  const tabs = [
    { id: 'manage-spaces', label: 'Manage Spaces', icon: Building },
    { id: 'configure-form', label: 'Configure Form', icon: FileText },
    { id: 'configure-services', label: 'Configure Services', icon: Package },
    { id: 'upload-documents', label: 'Upload Documents', icon: Upload },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'time', label: 'Time (24hr)' },
    { value: 'radio', label: 'Radio Button' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'description', label: 'Description Text' }
  ];

  const sections = [
    { value: 'form', label: 'Form Details' },
    { value: 'tour', label: 'Schedule Tour Fields' },
    { value: 'special_instructions', label: 'Special Instructions' }
  ];

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    if (token) {
      fetchSpaceCategories();
      fetchSpaces();
      fetchReservations();
      fetchSpaceSchedules();
      fetchBlockedDates();
      fetchFormFields();
      fetchSettings();
      fetchServiceCategories();
      fetchPaymentSettings();
      fetchDocumentFields();
      initializeDefaultFields();
    }
  }, [token]);

  // Helper function to calculate stay duration in hours
  const calculateStayDuration = (dropoffDate, dropoffTime, pickupDate, pickupTime) => {
    if (!dropoffDate || !dropoffTime || !pickupDate || !pickupTime) {
      return 0;
    }
    
    const checkIn = new Date(`${dropoffDate}T${dropoffTime}`);
    const checkOut = new Date(`${pickupDate}T${pickupTime}`);
    
    const durationMs = checkOut - checkIn;
    const durationHours = durationMs / (1000 * 60 * 60);
    
    return Math.max(0, durationHours);
  };

  // Helper function to calculate variant price based on duration
  const calculateVariantPrice = (service, durationHours) => {
    if (!service?.is_variable || !service?.variants || service.variants.length === 0) {
      return service?.price || 0;
    }

    if (durationHours <= 0) {
      return 0;
    }

    const variants = service.variants
      .filter(v => v.is_enabled)
      .map(v => {
        const variantName = v.variant_name.toLowerCase().replace(/\s+/g, '');
        let hours = 0;
        
        if (variantName.includes('hr')) {
          const match = variantName.match(/(\d+\.?\d*)hr/);
          if (match) {
            hours = parseFloat(match[1]);
          }
        } else if (variantName.includes('min')) {
          const match = variantName.match(/(\d+\.?\d*)min/);
          if (match) {
            hours = parseFloat(match[1]) / 60;
          }
        }
        
        return { hours, price: v.price, name: v.variant_name };
      })
      .sort((a, b) => b.hours - a.hours);

    let remainingHours = durationHours;
    let totalPrice = 0;

    for (const variant of variants) {
      if (variant.hours > 0) {
        const count = Math.floor(remainingHours / variant.hours);
        if (count > 0) {
          totalPrice += count * variant.price;
          remainingHours -= count * variant.hours;
        }
      }
    }

    if (remainingHours > 0 && variants.length > 0) {
      const smallestVariant = variants[variants.length - 1];
      const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
      totalPrice += additionalCount * smallestVariant.price;
    }

    return totalPrice;
  };

  // Helper function to get variant breakdown
  const getVariantBreakdown = (service, durationHours) => {
    if (!service?.is_variable || !service?.variants || service.variants.length === 0) {
      return [];
    }

    if (durationHours <= 0) {
      return [];
    }

    const variants = service.variants
      .filter(v => v.is_enabled)
      .map(v => {
        const variantName = v.variant_name.toLowerCase().replace(/\s+/g, '');
        let hours = 0;
        
        if (variantName.includes('hr')) {
          const match = variantName.match(/(\d+\.?\d*)hr/);
          if (match) {
            hours = parseFloat(match[1]);
          }
        } else if (variantName.includes('min')) {
          const match = variantName.match(/(\d+\.?\d*)min/);
          if (match) {
            hours = parseFloat(match[1]) / 60;
          }
        }
        
        return { hours, price: v.price, name: v.variant_name };
      })
      .sort((a, b) => b.hours - a.hours);

    let remainingHours = durationHours;
    const breakdown = [];

    for (const variant of variants) {
      if (variant.hours > 0) {
        const count = Math.floor(remainingHours / variant.hours);
        if (count > 0) {
          remainingHours -= count * variant.hours;
          breakdown.push({ ...variant, count });
        }
      }
    }

    if (remainingHours > 0 && variants.length > 0) {
      const smallestVariant = variants[variants.length - 1];
      const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
      const existingEntry = breakdown.find(b => b.name === smallestVariant.name);
      if (existingEntry) {
        existingEntry.count += additionalCount;
      } else {
        breakdown.push({ ...smallestVariant, count: additionalCount });
      }
    }

    return breakdown;
  };

  // Helper function to get field name from ID
  const getFieldNameById = (fieldId, section = 'form') => {
    let fields = [];
    if (section === 'form') fields = formFields;
    else if (section === 'room') fields = roomFields;
    else if (section === 'special') fields = specialInstructionFields;
    
    const field = fields.find(f => f.id === fieldId);
    return field?.field_name || fieldId;
  };

  const initializeDefaultFields = () => {
    // Initialize default First Name and Last Name fields for form section
    const defaultFormFields = [
      {
        id: 'default_first_name',
        field_name: 'First Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'form',
        order: 0
      },
      {
        id: 'default_last_name',
        field_name: 'Last Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'form',
        order: 1
      }
    ];
    
    // Initialize default First Name and Last Name fields for tour section
    const defaultTourFields = [
      {
        id: 'default_tour_first_name',
        field_name: 'First Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'tour',
        order: 0
      },
      {
        id: 'default_tour_last_name',
        field_name: 'Last Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'tour',
        order: 1
      }
    ];
    
    setFormFields(prev => {
      const hasDefaults = prev.some(f => f.is_system);
      if (!hasDefaults && prev.length === 0) {
        return defaultFormFields;
      }
      return prev;
    });
    
    setTourFields(prev => {
      const hasDefaults = prev.some(f => f.is_system);
      if (!hasDefaults && prev.length === 0) {
        return defaultTourFields;
      }
      return prev;
    });
  };

  // Manage Spaces Functions
  const fetchSpaceCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding-space-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.categories) {
        setSpaceCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching space categories:', error);
    }
  };

  const fetchSpaces = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding-spaces`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.spaces) {
        setSpaces(response.data.spaces);
      }
    } catch (error) {
      console.error('Error fetching spaces:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding-reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.reservations) {
        setReservations(response.data.reservations);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  };

  // Helper to check if a space is reserved on a specific date
  const getReservationsForSpaceAndDate = (spaceId, date) => {
    const dateStr = formatDateForAPI(date);
    return reservations.filter(res => {
      if (res.assigned_space_id !== spaceId) return false;
      
      // Check if this date falls within the reservation period
      const dropoffDate = res.dropoff_date;
      const pickupDate = res.pickup_date;
      
      return dateStr >= dropoffDate && dateStr <= pickupDate;
    });
  };

  // Helper to check if a space has any reservations on a date
  const isSpaceReserved = (spaceId, date) => {
    return getReservationsForSpaceAndDate(spaceId, date).length > 0;
  };

  // Open reservation details modal
  const handleViewReservation = async (reservation) => {
    setSelectedReservation(reservation);
    setShowReservationModal(true);
    setIsEditMode(false);
    setModalDataReady(false);

    try {
      // Fetch boarding settings to get service-category mappings
      const settingsResponse = await axios.get(`${API_BASE_URL}/api/boarding-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settings = settingsResponse.data?.settings || {};
      const mappings = settings.service_category_mappings || {};
      
      // Get service IDs with non-empty mappings
      const serviceIdsWithMappings = Object.keys(mappings).filter(serviceId => {
        const categories = mappings[serviceId];
        return Array.isArray(categories) && categories.length > 0;
      });

      // Fetch services
      let servicesWithMappings = [];
      if (serviceIdsWithMappings.length > 0) {
        const allServiceCategoriesResponse = await axios.get(`${API_BASE_URL}/api/appointment-service-menu-categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (allServiceCategoriesResponse.data?.success) {
          const serviceCategories = allServiceCategoriesResponse.data.menu_categories || [];
          const servicePromises = serviceCategories.map(category =>
            axios.get(`${API_BASE_URL}/api/appointment-service-categories/${category.id}/services`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: { services: [] } }))
          );

          const servicesResponses = await Promise.all(servicePromises);
          const allServices = servicesResponses.flatMap(res => res.data?.services || []);
          servicesWithMappings = allServices.filter(service => serviceIdsWithMappings.includes(service.id));
        }
      }

      setModalServices(servicesWithMappings);
      setModalSpaces(spaces);

      const serviceId = reservation.selected_service_id || reservation.selected_service?.id;
      
      // Get full service object from modalServices to include options
      const fullServiceObj = servicesWithMappings.find(s => s.id === serviceId);
      const serviceWithOptions = fullServiceObj ? {
        ...fullServiceObj,
        selectedOptions: reservation.selected_service?.selectedOptions || {}
      } : reservation.selected_service;
      
      // Filter spaces based on service
      const linkedCategories = mappings[serviceId] || [];
      const categoryArray = Array.isArray(linkedCategories) ? linkedCategories : [];
      let relevantSpaces = spaces;
      if (categoryArray.length > 0) {
        relevantSpaces = spaces.filter(space => categoryArray.includes(space.category_id));
      }
      if (reservation.assigned_space_id) {
        const currentSpace = spaces.find(s => s.id === reservation.assigned_space_id);
        if (currentSpace && !relevantSpaces.find(s => s.id === reservation.assigned_space_id)) {
          relevantSpaces = [currentSpace, ...relevantSpaces];
        }
      }
      setFilteredSpaces(relevantSpaces);

      console.log('=== SERVICE WITH OPTIONS DEBUG ===');
      console.log('Service ID:', serviceId);
      console.log('Full Service Object:', fullServiceObj);
      console.log('Has Options:', fullServiceObj?.has_options);
      console.log('Options Array:', fullServiceObj?.options);
      console.log('Options Length:', fullServiceObj?.options?.length);
      console.log('Service With Options:', serviceWithOptions);
      console.log('Modal Services:', modalServices.map(s => ({ id: s.id, name: s.service_name || s.name, hasOptions: s.options?.length || 0 })));
      
      setEditForm({
        dropoff_date: reservation.dropoff_date,
        dropoff_time: reservation.dropoff_time,
        pickup_date: reservation.pickup_date,
        pickup_time: reservation.pickup_time,
        assigned_space_id: reservation.assigned_space_id,
        selected_service_id: serviceId,
        selected_service: serviceWithOptions,
        status: reservation.status,
        form_details: reservation.form_details || {},
        room_details: reservation.room_details || {},
        special_instructions: reservation.special_instructions || {}
      });

      setModalDataReady(true);
    } catch (error) {
      console.error('Error loading reservation data:', error);
      setModalDataReady(true);
    }
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setErrorMessage('');
    
    // Reset form to original values
    const serviceId = selectedReservation.selected_service?.id;
    const fullServiceObj = modalServices.find(s => s.id === serviceId);
    const serviceWithOptions = fullServiceObj ? {
      ...fullServiceObj,
      selectedOptions: selectedReservation.selected_service?.selectedOptions || {}
    } : selectedReservation.selected_service;
    
    setEditForm({
      dropoff_date: selectedReservation.dropoff_date,
      dropoff_time: selectedReservation.dropoff_time,
      pickup_date: selectedReservation.pickup_date,
      pickup_time: selectedReservation.pickup_time,
      assigned_space_id: selectedReservation.assigned_space_id,
      selected_service_id: serviceId,
      selected_service: serviceWithOptions,
      status: selectedReservation.status
    });
  };

  const handleOptionChange = (optionName, value) => {
    setSelectedServiceOptions({
      ...selectedServiceOptions,
      [optionName]: value
    });
  };

  const handleOptionsConfirm = () => {
    // Save options to edit form
    setEditForm({
      ...editForm,
      selected_service: {
        ...editForm.selected_service,
        selectedOptions: selectedServiceOptions
      }
    });
    setShowOptionsModal(false);
  };

  const handleSaveReservationEdit = async () => {
    try {
      setIsUpdating(true); // Show loading modal
      
      // Check if service or dates/times changed
      const serviceChanged = editForm.selected_service_id !== selectedReservation.selected_service?.id;
      const datesChanged = 
        editForm.dropoff_date !== selectedReservation.dropoff_date ||
        editForm.dropoff_time !== selectedReservation.dropoff_time ||
        editForm.pickup_date !== selectedReservation.pickup_date ||
        editForm.pickup_time !== selectedReservation.pickup_time;

      let assignedSpaceId = editForm.assigned_space_id;

      // If service or dates changed, check availability and find a new space
      if (serviceChanged || datesChanged) {
        console.log('Service or dates changed, checking availability...');
        
        // Call check-availability API
        const availabilityResponse = await axios.post(
          `${API_BASE_URL}/api/boarding/check-availability`,
          {
            check_in_date: editForm.dropoff_date,
            check_in_time: editForm.dropoff_time,
            check_out_date: editForm.pickup_date,
            check_out_time: editForm.pickup_time,
            exclude_reservation_id: selectedReservation.id // Exclude current reservation from availability check
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('Availability response:', availabilityResponse.data);

        // Find the selected service in available services
        const availableServices = availabilityResponse.data.available_services || [];
        const selectedService = availableServices.find(s => s.id === editForm.selected_service_id);

        if (!selectedService) {
          setIsUpdating(false); // Hide loading modal
          const serviceName = editForm.selected_service?.service_name || editForm.selected_service?.name || 'this service';
          
          // Show custom error modal
          setErrorMessage(`${serviceName} is not available for the selected dates and times. Please select different dates or times.`);
          setShowErrorModal(true);
          return;
        }

        // Check if there's an available space
        if (!selectedService.assigned_space_id) {
          setIsUpdating(false); // Hide loading modal
          const serviceName = editForm.selected_service?.service_name || editForm.selected_service?.name || 'this service';
          
          // Show custom error modal
          setErrorMessage(`No space available for ${serviceName} for the selected dates and times. Please select different dates or times.`);
          setShowErrorModal(true);
          return;
        }

        // Use the assigned space from availability check
        assignedSpaceId = selectedService.assigned_space_id;
        console.log('New space assigned:', assignedSpaceId);
      }

      // Update the reservation
      await axios.patch(
        `${API_BASE_URL}/api/boarding-reservations/${selectedReservation.id}`,
        {
          dropoff_date: editForm.dropoff_date,
          dropoff_time: editForm.dropoff_time,
          pickup_date: editForm.pickup_date,
          pickup_time: editForm.pickup_time,
          assigned_space_id: assignedSpaceId,
          selected_service_id: editForm.selected_service_id,
          status: editForm.status
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showMessage('success', 'Reservation updated successfully');
      setIsEditMode(false);
      fetchReservations(); // This will refresh the calendar and free up old space
      setShowReservationModal(false);
      setIsUpdating(false); // Hide loading modal
    } catch (error) {
      console.error('Error updating reservation:', error);
      showMessage('error', error.response?.data?.message || 'Failed to update reservation');
      setIsUpdating(false); // Hide loading modal on error
    }
  };

  const handleServiceChange = (serviceId) => {
    const selectedServiceObj = modalServices.find(s => s.id === serviceId);
    
    // Update the service first
    setEditForm({
      ...editForm,
      selected_service_id: serviceId,
      selected_service: selectedServiceObj,
      assigned_space_id: ''
    });
    
    // If service has options, show options modal
    if (selectedServiceObj?.has_options && selectedServiceObj?.options?.length > 0) {
      // Try to carry over previous selections
      const previousOptions = selectedReservation.selected_service?.selectedOptions || {};
      const newOptions = {};
      
      // Map previous selections to new service options where option names match
      selectedServiceObj.options.forEach(option => {
        const previousValue = previousOptions[option.option_name];
        if (previousValue !== undefined) {
          newOptions[option.option_name] = previousValue;
        }
      });
      
      setSelectedServiceOptions(newOptions);
      setShowOptionsModal(true);
    }
  };

  const fetchSpaceSchedules = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding-space-schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.schedules) {
        setSpaceSchedules(response.data.schedules);
      }
    } catch (error) {
      console.error('Error fetching space schedules:', error);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding-space-blocked-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Blocked dates fetched:', response.data);
      if (response.data) {
        setBlockedDates(response.data);
        console.log('Blocked dates state updated with', response.data.length, 'entries');
      }
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const handleAddSpace = async () => {
    if (!spaceForm.space_name) {
      showMessage('error', 'Space name is required');
      return;
    }

    setLoading(true);
    try {
      let spaceId;
      
      if (editingSpace) {
        // Update existing space
        const updateData = {
          space_name: spaceForm.space_name,
          description: spaceForm.description,
          category_id: spaceForm.category_id || null,
          component_display_enabled: spaceForm.component_display_enabled
        };
        
        await axios.put(`${API_BASE_URL}/api/boarding-spaces/${editingSpace.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update default schedule settings for all schedules of this space
        const schedulesToUpdate = spaceSchedules.filter(s => s.space_id === editingSpace.id);
        const scheduleUpdateData = {
          start_time: spaceForm.is_24_hours ? '00:00' : spaceForm.start_time,
          end_time: spaceForm.is_24_hours ? '23:59' : spaceForm.end_time,
          slot_duration_minutes: spaceForm.enable_slot_booking ? spaceForm.slot_duration_minutes : 1440,
        };
        
        // Update each schedule with new default settings
        const updatePromises = schedulesToUpdate.map(schedule => 
          axios.put(`${API_BASE_URL}/api/boarding-space-schedules/${schedule.id}`, {
            ...schedule,
            ...scheduleUpdateData
          }, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => {
            console.error(`Error updating schedule ${schedule.id}:`, err);
            return null;
          })
        );
        
        await Promise.all(updatePromises);
        
        showMessage('success', 'Space and schedules updated successfully');
      } else {
        // Create new space
        const createData = {
          space_name: spaceForm.space_name,
          description: spaceForm.description,
          category_id: spaceForm.category_id || null,
          component_display_enabled: spaceForm.component_display_enabled
        };
        
        const response = await axios.post(`${API_BASE_URL}/api/boarding-spaces`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        spaceId = response.data.space?.id;
        
        // Create schedules for the duration
        if (spaceId) {
          await createSchedulesForSpace(spaceId);
          
          // Save temporarily blocked dates for new space
          if (tempBlockedDates.length > 0) {
            try {
              await axios.post(
                `${API_BASE_URL}/api/boarding-space-blocked-dates/bulk`,
                {
                  space_id: spaceId,
                  dates: tempBlockedDates,
                  is_blocked: true,
                  reason: 'Pre-blocked during space creation'
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setTempBlockedDates([]);
            } catch (error) {
              console.error('Error saving blocked dates:', error);
            }
          }
        }
        
        showMessage('success', 'Space and schedules created successfully');
      }
      
      fetchSpaces();
      fetchSpaceSchedules();
      fetchBlockedDates();
      setShowSpaceModal(false);
      setSpaceForm({ 
        space_name: '', 
        description: '',
        category_id: '',
        schedule_duration_months: 1,
        is_24_hours: false,
        start_time: '09:00',
        end_time: '17:00',
        slot_duration_minutes: 30,
        enable_slot_booking: false,
        bulk_start_date: '',
        bulk_end_date: ''
      });
      setEditingSpace(null);
    } catch (error) {
      console.error('Error saving space:', error);
      showMessage('error', 'Failed to save space');
    } finally {
      setLoading(false);
    }
  };

  const createSchedulesForSpace = async (spaceId) => {
    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const scheduleData = {
      start_time: spaceForm.is_24_hours ? '00:00' : spaceForm.start_time,
      end_time: spaceForm.is_24_hours ? '23:59' : spaceForm.end_time,
      slot_duration_minutes: spaceForm.enable_slot_booking ? spaceForm.slot_duration_minutes : 1440, // 1440 min = 24 hours (full day)
      is_enabled: true,
      breaks: []
    };

    // Create schedule for each day of the week
    const createPromises = daysOfWeek.map(day => {
      return axios.post(`${API_BASE_URL}/api/boarding-space-schedules`, {
        space_id: spaceId,
        day_of_week: day,
        ...scheduleData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => {
        console.error(`Error creating schedule for ${day}:`, err);
        return null;
      });
    });

    await Promise.all(createPromises);
  };

  const handleDeleteSpace = async (spaceId) => {
    if (!window.confirm('Are you sure you want to delete this space? All schedules for this space will also be deleted.')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/boarding-spaces/${spaceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Space deleted successfully');
      fetchSpaces();
      fetchSpaceSchedules();
    } catch (error) {
      console.error('Error deleting space:', error);
      showMessage('error', 'Failed to delete space');
    } finally {
      setLoading(false);
    }
  };

  // Space Category Management Functions
  const handleSaveCategory = async () => {
    if (!categoryForm.category_name.trim()) {
      showMessage('error', 'Category name is required');
      return;
    }

    setLoading(true);
    try {
      if (editingCategory) {
        await axios.put(`${API_BASE_URL}/api/boarding-space-categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Category updated successfully');
        setEditingCategory(null);
      } else {
        await axios.post(`${API_BASE_URL}/api/boarding-space-categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Category added successfully');
      }
      
      // Reset form but keep modal open to allow adding more categories
      setCategoryForm({ category_name: '', description: '' });
      fetchSpaceCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save category';
      showMessage('error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/boarding-space-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Category deleted successfully');
      fetchSpaceCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete category';
      showMessage('error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!selectedSpaceId || !editingSchedule?.day_of_week) {
      showMessage('error', 'Space and day are required');
      return;
    }

    setLoading(true);
    try {
      if (editingSchedule.id) {
        // Update existing schedule
        await axios.put(`${API_BASE_URL}/api/boarding-space-schedules/${editingSchedule.id}`, scheduleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Schedule updated successfully');
      } else {
        // Create new schedule
        const newSchedule = {
          space_id: selectedSpaceId,
          day_of_week: editingSchedule.day_of_week,
          ...scheduleForm
        };
        
        await axios.post(`${API_BASE_URL}/api/boarding-space-schedules`, newSchedule, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Schedule created successfully');
      }
      
      fetchSpaceSchedules();
      setShowScheduleModal(false);
      setScheduleForm({ start_time: '09:00', end_time: '17:00', slot_duration_minutes: 30, is_enabled: true, breaks: [] });
      setEditingSchedule(null);
      setSelectedSpaceId(null);
    } catch (error) {
      console.error('Error saving schedule:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setLoading(false);
    }
  };

  // Bulk calendar update handler
  const handleBulkCalendarUpdate = async (action) => {
    if (!spaceForm.bulk_start_date || !spaceForm.bulk_end_date) {
      showMessage('error', 'Please select both start and end dates');
      return;
    }

    const startDate = new Date(spaceForm.bulk_start_date);
    const endDate = new Date(spaceForm.bulk_end_date);

    if (endDate < startDate) {
      showMessage('error', 'End date must be after start date');
      return;
    }

    const dates = [];
    const currentDate = new Date(startDate);
    
    // Generate all dates in the range
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // If editing an existing space, save immediately
    if (editingSpace?.id) {
      setLoading(true);
      try {
        // Call the bulk update endpoint
        const isBlocked = action === 'block';
        const response = await axios.post(
          `${API_BASE_URL}/api/boarding-space-blocked-dates/bulk`,
          {
            space_id: editingSpace.id,
            dates: dates,
            is_blocked: isBlocked,
            reason: isBlocked ? 'Blocked by admin' : 'Scheduled/Available for booking'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showMessage('success', `Successfully ${action === 'block' ? 'blocked' : 'scheduled'} ${response.data.updated_count} dates`);
      
      // Clear the date inputs
      setSpaceForm({ 
        ...spaceForm, 
        bulk_start_date: '', 
        bulk_end_date: '' 
      });
      
      // Refresh data
      fetchSpaces();
      fetchSpaceSchedules();
      fetchBlockedDates();
    } catch (error) {
      console.error('Error in bulk update:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to update dates');
    } finally {
      setLoading(false);
    }
    } else {
      // For new space, store dates temporarily
      if (action === 'block') {
        setTempBlockedDates(dates);
        showMessage('success', `${dates.length} date(s) will be blocked when space is created`);
        
        // Clear the date inputs
        setSpaceForm({ 
          ...spaceForm, 
          bulk_start_date: '', 
          bulk_end_date: '' 
        });
      }
    }
  };

  // Week navigation functions
  const getWeekDates = () => {
    const dates = [];
    const start = new Date(currentWeekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    setCurrentWeekStart(newStart);
  };

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
  };

  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getScheduleForDay = (spaceId, dayOfWeek) => {
    return spaceSchedules.find(s => s.space_id === spaceId && s.day_of_week === dayOfWeek);
  };

  const getDateStatus = (spaceId, date) => {
    const dateStr = formatDateForAPI(date);
    const dateEntry = blockedDates.find(
      bd => bd.space_id === spaceId && bd.date === dateStr
    );
    if (dateEntry) {
      console.log(`Found date status for ${dateStr}:`, dateEntry);
    }
    return dateEntry; // Returns the entry or undefined
  };

  const isDateBlocked = (spaceId, date) => {
    const dateEntry = getDateStatus(spaceId, date);
    return dateEntry?.is_blocked === true;
  };

  const isDateScheduled = (spaceId, date) => {
    const dateEntry = getDateStatus(spaceId, date);
    return dateEntry?.is_blocked === false;
  };

  const toggleDateBlock = async (spaceId, date) => {
    const dateStr = formatDateForAPI(date);
    const dateEntry = getDateStatus(spaceId, date);
    const currentlyBlocked = dateEntry?.is_blocked === true;
    const currentlyScheduled = dateEntry?.is_blocked === false;
    
    try {
      // Toggle logic: blocked -> scheduled, scheduled/no-entry -> blocked
      const newIsBlocked = !currentlyBlocked;
      
      await axios.post(
        `${API_BASE_URL}/api/boarding-space-blocked-dates/bulk`,
        {
          space_id: spaceId,
          dates: [dateStr],
          is_blocked: newIsBlocked,
          reason: newIsBlocked ? 'Blocked by admin' : 'Scheduled/Available for booking'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchBlockedDates();
      showMessage('success', `Date ${newIsBlocked ? 'blocked' : 'scheduled'} successfully`);
    } catch (error) {
      console.error('Error toggling date block:', error);
      showMessage('error', 'Failed to update date');
    }
  };

  const fetchFormFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding/form-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormFields(response.data.filter(f => f.section === 'form'));
        setTourFields(response.data.filter(f => f.section === 'tour'));
        setRoomFields(response.data.filter(f => f.section === 'rooms'));
        setSpecialInstructionFields(response.data.filter(f => f.section === 'special_instructions'));
      }
    } catch (error) {
      console.error('Error fetching form fields:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(response.data);
        setRoomDetailsEnabled(response.data.room_details_enabled !== false);
        setSpecialInstructionsEnabled(response.data.special_instructions_enabled !== false);
        // Load selected service categories
        if (response.data.selected_service_categories) {
          setSelectedCategories(response.data.selected_service_categories);
        }
        // Load service-category mappings
        if (response.data.service_category_mappings) {
          setServiceCategoryMappings(response.data.service_category_mappings);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchDocumentFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding-document-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.fields) {
        setDocumentFields(response.data.fields);
      }
    } catch (error) {
      console.error('Error fetching document fields:', error);
    }
  };

  const fetchServiceCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-service-menu-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.menu_categories) {
        setServiceCategories(response.data.menu_categories);
        // Fetch services for each category
        response.data.menu_categories.forEach(cat => {
          fetchCategoryServices(cat.id);
        });
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
    }
  };

  const fetchCategoryServices = async (menuCategoryId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-services?menu_category_id=${menuCategoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.services) {
        setCategoryServices(prev => ({ ...prev, [menuCategoryId]: response.data.services }));
      }
    } catch (error) {
      console.error('Error fetching category services:', error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.success && response.data.gateways) {
        const activeModes = [];
        response.data.gateways.forEach(gateway => {
          if (gateway.enabled) {
            const label = gateway.gateway.charAt(0).toUpperCase() + gateway.gateway.slice(1);
            activeModes.push({ 
              value: gateway.gateway, 
              label: label 
            });
          }
        });
        setAvailablePaymentModes(activeModes);
      }
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
    }
  };

  const handleAddField = () => {
    if (!fieldForm.field_name) {
      showMessage('error', 'Field name is required');
      return;
    }

    const currentFields = selectedSection === 'form' ? formFields : 
                          selectedSection === 'tour' ? tourFields :
                          selectedSection === 'rooms' ? roomFields : specialInstructionFields;
    
    const newField = {
      id: editingField ? editingField.id : `field_${Date.now()}`,
      ...fieldForm,
      section: selectedSection,
      order: editingField ? editingField.order : currentFields.length,
      is_system: false
    };

    if (editingField) {
      // Update existing field
      if (selectedSection === 'form') {
        setFormFields(formFields.map(f => f.id === editingField.id ? newField : f));
      } else if (selectedSection === 'tour') {
        setTourFields(tourFields.map(f => f.id === editingField.id ? newField : f));
      } else if (selectedSection === 'rooms') {
        setRoomFields(roomFields.map(f => f.id === editingField.id ? newField : f));
      } else {
        setSpecialInstructionFields(specialInstructionFields.map(f => f.id === editingField.id ? newField : f));
      }
      showMessage('success', 'Field updated successfully');
    } else {
      // Add new field
      if (selectedSection === 'form') {
        setFormFields([...formFields, newField]);
      } else if (selectedSection === 'tour') {
        setTourFields([...tourFields, newField]);
      } else if (selectedSection === 'rooms') {
        setRoomFields([...roomFields, newField]);
      } else {
        setSpecialInstructionFields([...specialInstructionFields, newField]);
      }
      showMessage('success', 'Field added successfully');
    }

    resetFieldForm();
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldForm({
      field_name: field.field_name,
      field_type: field.field_type,
      required: field.required,
      placeholder: field.placeholder || '',
      description: field.description || '',
      options: field.options || ''
    });
    setShowAddFieldModal(true);
  };

  const handleDeleteField = (fieldId) => {
    // Find the field to check if it's a system field
    const allFields = selectedSection === 'form' ? formFields : 
                      selectedSection === 'tour' ? tourFields :
                      selectedSection === 'rooms' ? roomFields : specialInstructionFields;
    const field = allFields.find(f => f.id === fieldId);
    
    // Prevent deletion of system fields (First Name and Last Name)
    if (field?.is_system) {
      showMessage('error', 'System fields (First Name, Last Name) cannot be deleted as they are mandatory.');
      return;
    }

    // Show custom confirmation modal
    setFieldToDelete({ fieldId, fieldName: field?.field_name });
    setShowDeleteFieldModal(true);
  };

  const confirmDeleteField = () => {
    if (!fieldToDelete) return;

    if (selectedSection === 'form') {
      setFormFields(formFields.filter(f => f.id !== fieldToDelete.fieldId));
    } else if (selectedSection === 'tour') {
      setTourFields(tourFields.filter(f => f.id !== fieldToDelete.fieldId));
    } else if (selectedSection === 'rooms') {
      setRoomFields(roomFields.filter(f => f.id !== fieldToDelete.fieldId));
    } else {
      setSpecialInstructionFields(specialInstructionFields.filter(f => f.id !== fieldToDelete.fieldId));
    }
    
    showMessage('success', 'Field deleted successfully');
    setShowDeleteFieldModal(false);
    setFieldToDelete(null);
  };

  const handleMoveField = (fieldId, direction) => {
    const currentFields = selectedSection === 'form' ? formFields : 
                          selectedSection === 'tour' ? tourFields :
                          selectedSection === 'rooms' ? roomFields : specialInstructionFields;
    const index = currentFields.findIndex(f => f.id === fieldId);
    if (index === -1) return;

    const newFields = [...currentFields];
    if (direction === 'up' && index > 0) {
      [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
    } else if (direction === 'down' && index < newFields.length - 1) {
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    }

    // Update order
    newFields.forEach((field, idx) => {
      field.order = idx;
    });

    if (selectedSection === 'form') {
      setFormFields(newFields);
    } else if (selectedSection === 'tour') {
      setTourFields(newFields);
    } else if (selectedSection === 'rooms') {
      setRoomFields(newFields);
    } else {
      setSpecialInstructionFields(newFields);
    }
  };

  const resetFieldForm = () => {
    setFieldForm({
      field_name: '',
      field_type: 'text',
      required: false,
      placeholder: '',
      description: '',
      options: ''
    });
    setEditingField(null);
    setShowAddFieldModal(false);
  };

  const handleAddDocument = () => {
    if (!documentForm.title) {
      showMessage('error', 'Document title is required');
      return;
    }

    const newDocument = {
      id: `doc_${Date.now()}`,
      ...documentForm,
      order: documentFields.length
    };

    setDocumentFields([...documentFields, newDocument]);
    setDocumentForm({ title: '', description: '' });
    setShowAddDocumentModal(false);
    showMessage('success', 'Document field added successfully');
  };

  const handleDeleteDocument = (docId) => {
    if (!window.confirm('Are you sure you want to delete this document field?')) return;
    setDocumentFields(documentFields.filter(d => d.id !== docId));
    showMessage('success', 'Document field deleted successfully');
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      // Save settings with all configurations
      const settingsData = {
        ...settings,
        room_details_enabled: roomDetailsEnabled,
        special_instructions_enabled: specialInstructionsEnabled,
        selected_service_categories: selectedCategories,  // Save selected categories
        service_category_mappings: serviceCategoryMappings  // Save service-category mappings
      };

      await axios.post(`${API_BASE_URL}/api/boarding/settings`, settingsData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Ensure system fields (First Name, Last Name) are always included for form section
      const systemFormFields = [
        {
          id: 'default_first_name',
          field_name: 'First Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'form',
          order: 0
        },
        {
          id: 'default_last_name',
          field_name: 'Last Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'form',
          order: 1
        }
      ];

      // Ensure system fields for tour section
      const systemTourFields = [
        {
          id: 'default_tour_first_name',
          field_name: 'First Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'tour',
          order: 0
        },
        {
          id: 'default_tour_last_name',
          field_name: 'Last Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'tour',
          order: 1
        }
      ];

      // Merge system fields with user-created fields, ensuring system fields come first
      const userFormFields = formFields.filter(f => !f.is_system);
      const userTourFields = tourFields.filter(f => !f.is_system);
      const fieldsToSave = [...systemFormFields, ...userFormFields, ...systemTourFields, ...userTourFields, ...roomFields, ...specialInstructionFields];

      // Save form fields
      await axios.post(`${API_BASE_URL}/api/boarding/form-fields`, { fields: fieldsToSave }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save document fields
      await axios.post(`${API_BASE_URL}/api/boarding/document-fields`, { documents: documentFields }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showMessage('success', 'All settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('error', 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllBookings = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/boarding/bookings/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'All boarding bookings deleted successfully');
      setShowDeleteAllBookingsModal(false);
      fetchReservations(); // Refresh the calendar
    } catch (error) {
      console.error('Error deleting bookings:', error);
      showMessage('error', 'Failed to delete bookings');
      setShowDeleteAllBookingsModal(false);
    }
  };

  const handleDeleteAllTours = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/boarding/tour-schedules/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'All tour schedules deleted successfully');
      setShowDeleteAllToursModal(false);
      fetchReservations(); // Refresh the calendar
    } catch (error) {
      console.error('Error deleting tour schedules:', error);
      showMessage('error', 'Failed to delete tour schedules');
      setShowDeleteAllToursModal(false);
    }
  };

  const handleResetCounter = async () => {
    if (!window.confirm('Are you sure you want to reset the boarding number counter?')) return;
    
    try {
      await axios.post(`${API_BASE_URL}/api/boarding/reset-counter`, {
        start_number: settings.boarding_number_start
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'Boarding number counter reset successfully');
    } catch (error) {
      console.error('Error resetting counter:', error);
      showMessage('error', 'Failed to reset counter');
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const getCurrentFields = () => {
    if (selectedSection === 'form') return formFields;
    if (selectedSection === 'tour') return tourFields;
    if (selectedSection === 'rooms') return roomFields;
    return specialInstructionFields;
  };

  const isSectionEnabled = () => {
    if (selectedSection === 'rooms') return roomDetailsEnabled;
    if (selectedSection === 'special_instructions') return specialInstructionsEnabled;
    return true; // form and tour are always enabled
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-8 py-4">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-lg mb-6 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Manage Boarding
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 p-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Manage Spaces Tab */}
          {activeTab === 'manage-spaces' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-semibold text-gray-900">Boarding Spaces</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryForm({ category_name: '', description: '' });
                      setShowCategoryModal(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="h-3.5 w-3.5" />
                    Manage Categories
                  </button>
                  <button
                    onClick={() => {
                      setEditingSpace(null);
                      setSpaceForm({ 
                        space_name: '', 
                        description: '',
                        category_id: '',
                        schedule_duration_months: 1,
                        is_24_hours: false,
                        start_time: '09:00',
                        end_time: '17:00',
                        slot_duration_minutes: 30,
                        enable_slot_booking: false,
                        bulk_start_date: '',
                        bulk_end_date: '',
                        component_display_enabled: false
                      });
                      setShowSpaceModal(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Space
                  </button>
                </div>
              </div>

              {/* Filter and Search Section */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search spaces by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="w-64">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Categories</option>
                    {spaceCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {spaces.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Building className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">No boarding spaces found</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Create spaces to manage boarding availability
                  </p>
                  <button
                    onClick={() => {
                      setEditingSpace(null);
                      setSpaceForm({ 
                        space_name: '', 
                        description: '',
                        category_id: '',
                        schedule_duration_months: 1,
                        is_24_hours: false,
                        start_time: '09:00',
                        end_time: '17:00',
                        slot_duration_minutes: 30,
                        enable_slot_booking: false,
                        bulk_start_date: '',
                        bulk_end_date: '',
                        component_display_enabled: false
                      });
                      setShowSpaceModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Your First Space
                  </button>
                </div>
              ) : (
                <div>
                  {/* Week Navigation */}
                  <div className="mb-4 flex items-center justify-between">
                    <button
                      onClick={() => navigateWeek(-1)}
                      className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Previous Week</span>
                    </button>
                    
                    <div className="text-center">
                      <h3 className="text-sm font-medium text-gray-900">
                        Week of {formatDateForDisplay(currentWeekStart)}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {formatDateForAPI(currentWeekStart)} to {formatDateForAPI(getWeekDates()[6])}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => navigateWeek(1)}
                      className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                    >
                      <span>Next Week</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <div className="min-w-full" style={{ minWidth: '800px' }}>
                      {/* Week Header */}
                      <div className="grid grid-cols-8 bg-gray-50 border-b border-gray-200">
                        <div className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Space
                        </div>
                        {getWeekDates().map((date, index) => (
                          <div key={index} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div>{formatDateForDisplay(date)}</div>
                            <div className="text-gray-400 font-normal text-xs">
                              {formatDateForAPI(date)}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Schedule Rows */}
                      <div className="bg-white divide-y divide-gray-200">
                        {spaces
                          .filter(space => {
                            // Filter by search query
                            const matchesSearch = searchQuery === '' || 
                              space.space_name.toLowerCase().includes(searchQuery.toLowerCase());
                            
                            // Filter by category
                            const matchesCategory = categoryFilter === '' || 
                              space.category_id === categoryFilter;
                            
                            return matchesSearch && matchesCategory;
                          })
                          .map((space) => (
                          <div key={space.id} className="grid grid-cols-8">
                            {/* Space Info */}
                            <div className="px-4 py-6 border-r border-gray-200">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {space.space_name}
                                  </span>
                                  {space.component_display_enabled && (
                                    <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-green-500 text-white flex items-center gap-0.5" title="Enabled for CMS Component Display">
                                      <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"/>
                                      </svg>
                                      CMS
                                    </span>
                                  )}
                                </div>
                                {space.category_name && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {space.category_name}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <label className="flex items-center gap-1">
                                    <input
                                      type="checkbox"
                                      checked={space.is_enabled !== false}
                                      onChange={async (e) => {
                                        try {
                                          await axios.put(
                                            `${API_BASE_URL}/api/boarding-spaces/${space.id}`,
                                            {
                                              space_name: space.space_name,
                                              description: space.description,
                                              is_enabled: e.target.checked
                                            },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          fetchSpaces();
                                          showMessage('success', `Space ${e.target.checked ? 'enabled' : 'disabled'} successfully`);
                                        } catch (error) {
                                          showMessage('error', 'Failed to update space');
                                        }
                                      }}
                                      className="rounded text-xs"
                                    />
                                    <span className="text-xs text-gray-600">On</span>
                                  </label>
                                  <button
                                    onClick={() => {
                                      // Get the first schedule for this space to read default settings
                                      const firstSchedule = spaceSchedules.find(s => s.space_id === space.id);
                                      const is24Hours = firstSchedule?.start_time === '00:00' && firstSchedule?.end_time === '23:59';
                                      const enableSlotBooking = firstSchedule?.slot_duration_minutes < 1440;
                                      
                                      setEditingSpace(space);
                                      setSpaceForm({
                                        space_name: space.space_name,
                                        description: space.description || '',
                                        category_id: space.category_id || '',
                                        schedule_duration_months: 1,
                                        is_24_hours: is24Hours,
                                        start_time: firstSchedule?.start_time || '09:00',
                                        end_time: firstSchedule?.end_time || '17:00',
                                        slot_duration_minutes: firstSchedule?.slot_duration_minutes || 30,
                                        enable_slot_booking: enableSlotBooking,
                                        bulk_start_date: '',
                                        bulk_end_date: '',
                                        component_display_enabled: space.component_display_enabled || false
                                      });
                                      setShowSpaceModal(true);
                                    }}
                                    className="p-1 text-theme-blue hover:bg-blue-50 rounded"
                                    title="Edit space"
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSpace(space.id)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete space"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Daily Schedule Cells */}
                            {getWeekDates().map((date, dateIndex) => {
                              const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                              const schedule = getScheduleForDay(space.id, dayOfWeek);
                              const isBlocked = isDateBlocked(space.id, date);
                              const isScheduled = isDateScheduled(space.id, date);
                              const reservationsOnDate = getReservationsForSpaceAndDate(space.id, date);
                              const hasReservation = reservationsOnDate.length > 0;
                              const hasMultipleReservations = reservationsOnDate.length > 1;
                              
                              // Determine reservation type for this date
                              const dateStr = formatDateForAPI(date);
                              let reservationInfo = null;
                              
                              if (hasReservation && reservationsOnDate.length === 1) {
                                const reservation = reservationsOnDate[0];
                                const isSameDay = reservation.dropoff_date === reservation.pickup_date;
                                const isCheckInDay = dateStr === reservation.dropoff_date;
                                const isCheckOutDay = dateStr === reservation.pickup_date;
                                const isMiddleDay = !isCheckInDay && !isCheckOutDay;
                                
                                reservationInfo = {
                                  reservation,
                                  isSameDay,
                                  isCheckInDay,
                                  isCheckOutDay,
                                  isMiddleDay,
                                  // For same-day: reserved only during actual hours
                                  // For check-in day: reserved from check-in time to end of day (12am)
                                  // For check-out day: reserved from start of day (12am) to check-out time
                                  // For middle day: fully reserved
                                  isPartialDay: isSameDay || isCheckInDay || isCheckOutDay
                                };
                              }
                              
                              return (
                                <div key={dateIndex} className="px-2 py-4 border-r border-gray-200 h-[140px]">
                                  {schedule ? (
                                    hasMultipleReservations ? (
                                      // Split cell for multiple reservations
                                      <div className="rounded-lg border border-dotted border-blue-400 h-full flex flex-col overflow-hidden">
                                        {/* First Reservation - Upper Half */}
                                        <div 
                                          className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center border-b border-blue-300"
                                          onClick={() => handleViewReservation(reservationsOnDate[0])}
                                        >
                                          <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                          <div className="text-[9px] text-blue-700">
                                            {reservationsOnDate[0].dropoff_time} - {reservationsOnDate[0].pickup_time}
                                          </div>
                                        </div>
                                        
                                        {/* Second Reservation - Lower Half */}
                                        <div 
                                          className="flex-1 bg-purple-100 hover:bg-purple-200 cursor-pointer flex flex-col items-center justify-center"
                                          onClick={() => handleViewReservation(reservationsOnDate[1])}
                                        >
                                          <div className="text-[10px] font-bold text-purple-900 mb-0.5">RESERVED</div>
                                          <div className="text-[9px] text-purple-700">
                                            {reservationsOnDate[1].dropoff_time} - {reservationsOnDate[1].pickup_time}
                                          </div>
                                        </div>
                                      </div>
                                    ) : reservationInfo?.isPartialDay ? (
                                      // Partial day: Split cell based on same-day, check-in, or check-out
                                      <div className="rounded-lg border border-dotted border-blue-400 h-full flex flex-col overflow-hidden">
                                        {reservationInfo.isSameDay ? (
                                          // Same-day reservation: Show actual reserved hours + remaining available
                                          <>
                                            {/* Reserved During Booking Hours - Upper Half */}
                                            <div 
                                              className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center border-b border-green-300"
                                              onClick={() => handleViewReservation(reservationInfo.reservation)}
                                            >
                                              <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                              <div className="text-[9px] text-blue-700">
                                                {reservationInfo.reservation.dropoff_time} - {reservationInfo.reservation.pickup_time}
                                              </div>
                                            </div>
                                            
                                            {/* Available Rest of Day - Lower Half */}
                                            <div className="flex-1 bg-green-100 flex flex-col items-center justify-center">
                                              <div className="text-[10px] font-bold text-green-900 mb-0.5">AVAILABLE</div>
                                              <div className="text-[9px] text-green-700">
                                                Rest of day
                                              </div>
                                            </div>
                                          </>
                                        ) : reservationInfo.isCheckInDay ? (
                                          // Check-in day: Available (12am to check-in) + Reserved (check-in to 12am)
                                          <>
                                            {/* Available Before Check-in - Upper Half */}
                                            <div className="flex-1 bg-green-100 flex flex-col items-center justify-center border-b border-blue-300">
                                              <div className="text-[10px] font-bold text-green-900 mb-0.5">AVAILABLE</div>
                                              <div className="text-[9px] text-green-700">
                                                12:00 AM - {reservationInfo.reservation.dropoff_time}
                                              </div>
                                            </div>
                                            
                                            {/* Reserved After Check-in - Lower Half */}
                                            <div 
                                              className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center"
                                              onClick={() => handleViewReservation(reservationInfo.reservation)}
                                            >
                                              <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                              <div className="text-[9px] text-blue-700">
                                                {reservationInfo.reservation.dropoff_time} - 12:00 AM
                                              </div>
                                            </div>
                                          </>
                                        ) : (
                                          // Check-out day: Reserved (12am to check-out) + Available (check-out to 12am)
                                          <>
                                            {/* Reserved Until Check-out - Upper Half */}
                                            <div 
                                              className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center border-b border-green-300"
                                              onClick={() => handleViewReservation(reservationInfo.reservation)}
                                            >
                                              <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                              <div className="text-[9px] text-blue-700">
                                                12:00 AM - {reservationInfo.reservation.pickup_time}
                                              </div>
                                            </div>
                                            
                                            {/* Available After Check-out - Lower Half */}
                                            <div className="flex-1 bg-green-100 flex flex-col items-center justify-center">
                                              <div className="text-[10px] font-bold text-green-900 mb-0.5">AVAILABLE</div>
                                              <div className="text-[9px] text-green-700">
                                                {reservationInfo.reservation.pickup_time} - 12:00 AM
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    ) : reservationInfo?.isMiddleDay ? (
                                      // Middle day of multi-day reservation: Fully reserved
                                      <div 
                                        className="rounded-lg border border-dotted border-blue-400 bg-blue-100 hover:bg-blue-200 cursor-pointer h-full flex flex-col items-center justify-center"
                                        onClick={() => handleViewReservation(reservationInfo.reservation)}
                                      >
                                        <div className="text-xs font-bold text-blue-900 mb-1">RESERVED</div>
                                        <div className="text-xs text-blue-700">Full Day</div>
                                        <div className="text-[9px] text-theme-blue mt-1">
                                          (Multi-day booking)
                                        </div>
                                      </div>
                                    ) : (
                                      // Single reservation or no reservation
                                      <div 
                                        className={`rounded-lg border border-dotted p-3 h-full flex flex-col ${
                                          hasReservation
                                            ? 'bg-blue-100 border-blue-400 cursor-pointer hover:bg-blue-200'
                                            : isBlocked 
                                              ? 'bg-red-50 border-red-300' 
                                              : isScheduled
                                                ? 'bg-green-100 border-green-400'
                                                : schedule.is_enabled 
                                                  ? 'bg-green-50 border-green-300' 
                                                  : 'bg-gray-50 border-gray-300'
                                        }`}
                                        onClick={() => {
                                          if (hasReservation) {
                                            handleViewReservation(reservationsOnDate[0]);
                                          }
                                        }}
                                      >
                                        <div className="flex-shrink-0 text-center mb-2">
                                          {hasReservation ? (
                                            <div>
                                              <div className="text-xs font-bold text-blue-900 mb-1">
                                                RESERVED
                                              </div>
                                              <div className="text-xs text-blue-700">
                                                {reservationsOnDate[0].dropoff_time} - {reservationsOnDate[0].pickup_time}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="text-xs font-semibold text-gray-900">
                                              {schedule.slot_duration_minutes >= 1440 ? '24 Hours' : `${schedule.start_time} - ${schedule.end_time}`}
                                            </div>
                                          )}
                                        </div>
                                        {schedule.breaks && schedule.breaks.length > 0 && (
                                          <div className="flex-shrink-0 text-xs text-gray-500 mb-2">
                                            {schedule.breaks.length} break{schedule.breaks.length > 1 ? 's' : ''}
                                          </div>
                                        )}
                                        <div className="flex-grow"></div>
                                        <div className="space-y-2 mt-2 flex-shrink-0">
                                        <div className="flex items-center justify-between">
                                          <label className="flex items-center gap-1">
                                            <input
                                              type="checkbox"
                                              checked={!isBlocked}
                                              onChange={async () => {
                                                try {
                                                  await axios.post(
                                                    `${API_BASE_URL}/api/boarding-space-blocked-dates/bulk`,
                                                    {
                                                      space_id: space.id,
                                                      dates: [formatDateForAPI(date)],
                                                      is_blocked: !isBlocked,
                                                      reason: isBlocked ? 'Scheduled/Available' : 'Blocked by admin'
                                                    },
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                  );
                                                  fetchBlockedDates();
                                                  showMessage('success', 'Date availability updated');
                                                } catch (error) {
                                                  showMessage('error', 'Failed to update date');
                                                }
                                              }}
                                              className="rounded text-xs"
                                            />
                                            <span className="text-xs text-gray-600">{isBlocked ? 'Blocked' : 'On'}</span>
                                          </label>
                                          <button
                                            onClick={() => {
                                              const dateEntry = getDateStatus(space.id, date);
                                              const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                              
                                              // Populate form with current schedule settings
                                              const is24Hours = schedule.slot_duration_minutes >= 1440 || (schedule.start_time === '00:00' && schedule.end_time === '23:59');
                                              const enableSlotBooking = schedule.slot_duration_minutes < 1440;
                                              
                                              setDateForm({
                                                is_24_hours: is24Hours,
                                                start_time: schedule.start_time || '09:00',
                                                end_time: schedule.end_time || '17:00',
                                                slot_duration_minutes: schedule.slot_duration_minutes || 30,
                                                enable_slot_booking: enableSlotBooking
                                              });
                                              
                                              setEditingDateEntry({ 
                                                ...dateEntry, 
                                                date: formatDateForAPI(date), 
                                                spaceId: space.id,
                                                dayOfWeek: dayOfWeek 
                                              });
                                              setShowDateModal(true);
                                            }}
                                            className="text-theme-blue hover:text-blue-700"
                                          >
                                            <Edit3 className="h-3 w-3" />
                                          </button>
                                        </div>
                                      </div>
                                      </div>
                                    )
                                  ) : (isBlocked || isScheduled) ? (
                                    // No base schedule but has date-specific block/schedule
                                    <div className={`rounded-lg border border-dotted p-3 h-full flex flex-col ${
                                      isBlocked 
                                        ? 'bg-red-50 border-red-300' 
                                        : 'bg-green-100 border-green-400'
                                    }`}>
                                      {isScheduled && (() => {
                                        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                        const schedule = getScheduleForDay(space.id, dayOfWeek);
                                        const is24Hours = schedule?.slot_duration_minutes >= 1440;
                                        
                                        return (
                                          <div className="flex-shrink-0 mb-2 text-center">
                                            <div className="text-xs font-semibold text-gray-900">
                                              {is24Hours ? '24 Hours' : `${schedule?.start_time || '09:00'} - ${schedule?.end_time || '17:00'}`}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      <div className="flex-grow"></div>
                                      <div className="flex items-center justify-between gap-2 flex-shrink-0">
                                        <label className="flex items-center gap-1">
                                          <input
                                            type="checkbox"
                                            checked={!isBlocked}
                                            onChange={async () => {
                                              try {
                                                await axios.post(
                                                  `${API_BASE_URL}/api/boarding-space-blocked-dates/bulk`,
                                                  {
                                                    space_id: space.id,
                                                    dates: [formatDateForAPI(date)],
                                                    is_blocked: !isBlocked,
                                                    reason: isBlocked ? 'Scheduled/Available' : 'Blocked by admin'
                                                  },
                                                  { headers: { Authorization: `Bearer ${token}` } }
                                                );
                                                fetchBlockedDates();
                                                showMessage('success', 'Date availability updated');
                                              } catch (error) {
                                                showMessage('error', 'Failed to update date');
                                              }
                                            }}
                                            className="rounded text-xs"
                                          />
                                          <span className="text-xs text-gray-600">{isBlocked ? 'Blocked' : 'On'}</span>
                                        </label>
                                        <button
                                          onClick={() => {
                                            const dateEntry = getDateStatus(space.id, date);
                                            const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                            const schedule = getScheduleForDay(space.id, dayOfWeek);
                                            
                                            // Populate form with current schedule settings
                                            const is24Hours = schedule?.slot_duration_minutes >= 1440 || (schedule?.start_time === '00:00' && schedule?.end_time === '23:59');
                                            const enableSlotBooking = schedule?.slot_duration_minutes < 1440;
                                            
                                            setDateForm({
                                              is_24_hours: is24Hours,
                                              start_time: schedule?.start_time || '09:00',
                                              end_time: schedule?.end_time || '17:00',
                                              slot_duration_minutes: schedule?.slot_duration_minutes || 30,
                                              enable_slot_booking: enableSlotBooking
                                            });
                                            
                                            setEditingDateEntry({ 
                                              ...dateEntry, 
                                              date: formatDateForAPI(date), 
                                              spaceId: space.id,
                                              dayOfWeek: dayOfWeek 
                                            });
                                            setShowDateModal(true);
                                          }}
                                          className="text-theme-blue hover:text-blue-700"
                                        >
                                          <Edit3 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="h-full">
                                      <button
                                        onClick={() => {
                                          const dateEntry = getDateStatus(space.id, date);
                                          const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                          
                                          // Set default form values for new schedule
                                          setDateForm({
                                            is_24_hours: false,
                                            start_time: '09:00',
                                            end_time: '17:00',
                                            slot_duration_minutes: 30,
                                            enable_slot_booking: true
                                          });
                                          
                                          setEditingDateEntry({ 
                                            ...dateEntry, 
                                            date: formatDateForAPI(date), 
                                            spaceId: space.id,
                                            dayOfWeek: dayOfWeek,
                                            is_blocked: false // Default to available when adding new schedule
                                          });
                                          setShowDateModal(true);
                                        }}
                                        className="w-full h-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg border border-dotted border-gray-300 transition-colors"
                                      >
                                        <Plus className="h-5 w-5" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Configure Form Tab */}
          {activeTab === 'configure-form' && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Configure Booking Form</h3>
              
              {/* Section Tabs with Add Button */}
              <div className="flex items-center justify-between mb-6 border-b">
                <div className="flex space-x-2">
                  {sections.map(section => (
                    <button
                      key={section.value}
                      onClick={() => setSelectedSection(section.value)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        selectedSection === section.value
                          ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
                
                {/* Add Field Button - Right side of tabs */}
                {isSectionEnabled() && (
                  <button
                    onClick={() => setShowAddFieldModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 transition-opacity mb-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Field
                  </button>
                )}
              </div>

              {/* Enable/Disable Toggle for Room Details and Special Instructions */}
              {selectedSection === 'special_instructions' && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Enable Special Instructions Section
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        When disabled, this section will not appear in the booking form
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={specialInstructionsEnabled}
                        onChange={(e) => {
                          setSpecialInstructionsEnabled(e.target.checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Fields List */}
              {isSectionEnabled() && (
                <>
                  <div className="space-y-2 mb-4">
                    {getCurrentFields().map((field, index) => (
                      <div key={field.id} className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-[var(--theme-primary)] transition-colors">
                        <div className="flex flex-col space-y-1 mr-3">
                          <button
                            onClick={() => handleMoveField(field.id, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-[var(--theme-primary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleMoveField(field.id, 'down')}
                            disabled={index === getCurrentFields().length - 1}
                            className="p-1 text-gray-400 hover:text-[var(--theme-primary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{field.field_name}</span>
                            {field.required && <span className="text-xs text-red-600 font-semibold">*</span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{capitalizeFirstLetter(field.field_type)}</p>
                          {field.description && (
                            <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {!field.is_system && (
                            <>
                              <button
                                onClick={() => handleEditField(field)}
                                className="p-2 text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteField(field.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {field.is_system && (
                            <span className="text-xs text-gray-400 italic">Default</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          )}

          {/* Configure Services Tab */}
          {activeTab === 'configure-services' && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Boarding Services</h3>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enable Service Selection</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Allow customers to select boarding services during reservation
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.services_enabled}
                      onChange={(e) => setSettings({ ...settings, services_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                  </label>
                </div>
              </div>

              {settings.services_enabled && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">Select Service Categories</p>
                  {serviceCategories.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No service categories found</p>
                      <p className="text-xs text-gray-400 mt-1">Create service categories in Service Management</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {serviceCategories.map(category => (
                        <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="p-4 bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={selectedCategories.includes(category.id)}
                                onChange={() => handleCategoryToggle(category.id)}
                                className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                              />
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{category.menu_category_name}</h4>
                                <p className="text-xs text-gray-500">
                                  {categoryServices[category.id]?.length || 0} service(s)
                                </p>
                              </div>
                            </div>
                          </div>
                          {selectedCategories.includes(category.id) && categoryServices[category.id] && (
                            <div className="p-4 bg-white border-t border-gray-200">
                              <p className="text-xs font-medium text-gray-700 mb-3">Services in this category:</p>
                              <div className="space-y-2">
                                {categoryServices[category.id].map(service => (
                                  <div key={service.id} className="p-3 bg-gray-50 rounded border border-gray-200 flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900 text-sm">{service.service_name}</p>
                                      <p className="text-gray-600 text-xs">{service.duration_minutes} min • ${service.price}</p>
                                    </div>
                                    
                                    {/* Space Category Selection */}
                                    <div className="flex-shrink-0 w-64">
                                      {spaceCategories.length === 0 ? (
                                        <p className="text-xs text-gray-500 italic">No space categories</p>
                                      ) : (
                                        <div className="space-y-1">
                                          <p className="text-xs font-medium text-gray-700 mb-1">Assign to Space Categories:</p>
                                          <div className="flex flex-wrap gap-2">
                                            {spaceCategories.map(spaceCategory => {
                                              const currentMappings = serviceCategoryMappings[service.id] || [];
                                              const isSelected = Array.isArray(currentMappings) 
                                                ? currentMappings.includes(spaceCategory.id)
                                                : currentMappings === spaceCategory.id;
                                              
                                              return (
                                                <label
                                                  key={spaceCategory.id}
                                                  className="inline-flex items-center"
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                      let newMappings = Array.isArray(currentMappings) 
                                                        ? [...currentMappings] 
                                                        : (currentMappings ? [currentMappings] : []);
                                                      
                                                      if (e.target.checked) {
                                                        if (!newMappings.includes(spaceCategory.id)) {
                                                          newMappings.push(spaceCategory.id);
                                                        }
                                                      } else {
                                                        newMappings = newMappings.filter(id => id !== spaceCategory.id);
                                                      }
                                                      
                                                      setServiceCategoryMappings({
                                                        ...serviceCategoryMappings,
                                                        [service.id]: newMappings
                                                      });
                                                    }}
                                                    className="w-3 h-3 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                                                  />
                                                  <span className="ml-1 text-xs text-gray-700">{spaceCategory.category_name}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          )}

          {/* Upload Documents Tab */}
          {activeTab === 'upload-documents' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Document Upload Configuration</h3>
                
                {/* Add Upload Field Button - Top Right */}
                {settings.document_upload_enabled && (
                  <button
                    onClick={() => setShowAddDocumentModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Upload Field
                  </button>
                )}
              </div>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enable Document Upload</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Allow customers to upload documents during booking
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.document_upload_enabled}
                      onChange={(e) => setSettings({ ...settings, document_upload_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                  </label>
                </div>
              </div>

              {settings.document_upload_enabled && (
                <div>
                  <div className="space-y-3 mb-4">
                    {documentFields.map((doc) => (
                      <div key={doc.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{doc.title}</h4>
                            {doc.description && (
                              <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                            )}
                            <div className="mt-3 flex items-center space-x-2 text-xs text-gray-500">
                              <Upload className="h-3 w-3" />
                              <span>Upload field will appear in booking form</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-4"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Boarding Settings</h3>

              {/* Slot Bookings Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('slotBookings')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Slot & Booking Settings</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.slotBookings ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.slotBookings ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 space-y-6 border-t border-gray-200">
                    {/* Drop-off and Pickup Timings */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-4">Drop-off and Pickup Timings</p>

              {/* Drop-off and Pickup Timings */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-900 mb-4">Drop-off & Pickup Time Ranges</p>
                <p className="text-xs text-gray-600 mb-4">Configure available time slots for drop-off and pickup (shown in 30-minute intervals)</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  {/* Drop-off Times */}
                  <div className="p-3 border border-gray-300 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 mb-3">Drop-off Time Range</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={settings.dropoff_start_time || '07:00'}
                          onChange={(e) => setSettings({ ...settings, dropoff_start_time: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                        <input
                          type="time"
                          value={settings.dropoff_end_time || '19:00'}
                          onChange={(e) => setSettings({ ...settings, dropoff_end_time: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Pickup Times */}
                  <div className="p-3 border border-gray-300 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 mb-3">Pickup Time Range</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={settings.pickup_start_time || '07:00'}
                          onChange={(e) => setSettings({ ...settings, pickup_start_time: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                        <input
                          type="time"
                          value={settings.pickup_end_time || '19:00'}
                          onChange={(e) => setSettings({ ...settings, pickup_end_time: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Slot Timing for Tour Appointments */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-900 mb-4">Schedule Tour Appointment Settings</p>
                <p className="text-xs text-gray-600 mb-4">Configure time slots for tour appointments (booking widget)</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                    <input
                      type="time"
                      value={settings.start_time}
                      onChange={(e) => setSettings({ ...settings, start_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                    <input
                      type="time"
                      value={settings.end_time}
                      onChange={(e) => setSettings({ ...settings, end_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Slot Duration (min)</label>
                    <input
                      type="number"
                      value={settings.slot_duration}
                      onChange={(e) => setSettings({ ...settings, slot_duration: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Allow Multiple Bookings Toggle */}
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Allow Multiple Bookings per Slot</p>
                    <p className="text-xs text-gray-500 mt-1">Enable multiple customers to book the same time slot</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allow_multiple_tour_bookings || false}
                      onChange={(e) => setSettings({ ...settings, allow_multiple_tour_bookings: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme-blue"></div>
                  </label>
                </div>

                {/* Service Provider Name Configuration */}
                <div className="p-3 bg-white border border-gray-200 rounded-lg mt-4">
                  <label className="block text-sm font-medium text-gray-900 mb-2">Service Provider</label>
                  <p className="text-xs text-gray-500 mb-3">Default service provider name for tour appointments</p>
                  <input
                    type="text"
                    value={settings.tour_service_provider || 'Manager'}
                    onChange={(e) => setSettings({ ...settings, tour_service_provider: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Manager"
                  />
                </div>
              </div>
                  </div>
                </div>
              </div>

              {/* Appointment Counter Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('appointmentCounter')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Hash className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Appointment Counter</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.appointmentCounter ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.appointmentCounter ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    {/* Boarding Number Configuration */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-4">Boarding Number Configuration</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                          <input
                            type="text"
                            value={settings.boarding_number_prefix}
                            onChange={(e) => setSettings({ ...settings, boarding_number_prefix: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="SPACE"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                          <input
                            type="number"
                            value={settings.boarding_number_start}
                            onChange={(e) => setSettings({ ...settings, boarding_number_start: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="1000"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={handleResetCounter}
                          className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Reset Counter
                        </button>
                        <button
                          onClick={() => setShowDeleteAllBookingsModal(true)}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        >
                          Delete All Bookings
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 mt-3">
                        Example: {settings.boarding_number_prefix}{settings.boarding_number_start}
                      </p>
                    </div>

                    {/* Tour Number Configuration */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mt-4">
                      <p className="text-sm font-medium text-gray-900 mb-4">Tour Appointment Number Configuration</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                          <input
                            type="text"
                            value={settings.tour_number_prefix || 'TOUR'}
                            onChange={(e) => setSettings({ ...settings, tour_number_prefix: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="TOUR"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                          <input
                            type="number"
                            value={settings.tour_number_start || 1000}
                            onChange={(e) => setSettings({ ...settings, tour_number_start: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="1000"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to reset the tour appointment counter? This will start numbering from the beginning.')) {
                              try {
                                await axios.post(`${API_BASE_URL}/api/boarding/reset-tour-counter`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                showMessage('success', 'Tour counter reset successfully');
                              } catch (error) {
                                console.error('Error resetting tour counter:', error);
                                showMessage('error', 'Failed to reset tour counter');
                              }
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Reset Tour Counter
                        </button>
                        <button
                          onClick={() => setShowDeleteAllToursModal(true)}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        >
                          Delete All Tour Schedules
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 mt-3">
                        Example: {settings.tour_number_prefix || 'TOUR'}{settings.tour_number_start || 1000}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Settings Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('payments')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Payment Settings</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.payments ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.payments ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                
                <div className="mb-4">
                  {/* Tour Payment Toggle */}
                  <div className="flex items-center justify-between mb-3 p-3 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Enable Payment for Tour Schedule</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Collect payments when customers schedule tour appointments</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.tour_payment_enabled || false}
                        onChange={(e) => setSettings({ ...settings, tour_payment_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    </label>
                  </div>

                  {/* Boarding Payment Toggle */}
                  <div className="flex items-center justify-between mb-3 p-3 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Enable Payment for Book Boarding</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Collect payments when customers book boarding appointments</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.boarding_payment_enabled || false}
                        onChange={(e) => setSettings({ ...settings, boarding_payment_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    </label>
                  </div>

                  {(settings.tour_payment_enabled || settings.boarding_payment_enabled) && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Gateway</label>
                        <select
                          value={settings.payment_mode}
                          onChange={(e) => setSettings({ ...settings, payment_mode: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">Select Payment Gateway</option>
                          {availablePaymentModes.map(mode => (
                            <option key={mode.value} value={mode.value}>{mode.label}</option>
                          ))}
                        </select>
                        {availablePaymentModes.length === 0 && (
                          <p className="text-xs text-red-600 mt-1">No payment gateways configured. Configure in Payment Settings.</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Token/Deposit Amount ($)</label>
                        <input
                          type="number"
                          value={settings.token_amount}
                          onChange={(e) => setSettings({ ...settings, token_amount: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

                    </div>
                  </div>
                </div>
              </div>

              {/* Notifications Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('notifications')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Notifications</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.notifications ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.notifications ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">Email Notifications</span>
                            <p className="text-xs text-gray-500">Send email updates for bookings</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.email_enabled}
                            onChange={(e) => setSettings({ ...settings, email_enabled: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          <MessageSquare className="h-5 w-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">SMS Notifications</span>
                            <p className="text-xs text-gray-500">Send SMS updates for bookings</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.sms_enabled}
                            onChange={(e) => setSettings({ ...settings, sms_enabled: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Settings Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('taxSettings')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Receipt className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Tax Settings</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.taxSettings ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.taxSettings ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm text-gray-700">Enable Tax</span>
                      <p className="text-xs text-gray-500">Apply sales tax to boarding bookings</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.tax_enabled || false}
                        onChange={(e) => setSettings({ ...settings, tax_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    </label>
                  </div>

                  {settings.tax_enabled && (
                    <div className="space-y-4 mt-4">
                      {/* Tax Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tax Configuration</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={(settings.tax_type || 'state') === 'state'}
                              onChange={() => setSettings({ ...settings, tax_type: 'state' })}
                              className="h-4 w-4"
                              style={{ accentColor: primaryColor }}
                            />
                            <span className="ml-2 text-sm text-gray-700">Use State Tax Rate</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={(settings.tax_type || 'state') === 'custom'}
                              onChange={() => setSettings({ ...settings, tax_type: 'custom' })}
                              className="h-4 w-4"
                              style={{ accentColor: primaryColor }}
                            />
                            <span className="ml-2 text-sm text-gray-700">Use Custom Tax Rate</span>
                          </label>
                        </div>
                      </div>

                      {/* State Selection or Custom Rate */}
                      {(settings.tax_type || 'state') === 'state' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select State</label>
                          <select
                            value={settings.tax_state || ''}
                            onChange={(e) => {
                              const stateCode = e.target.value;
                              const taxRates = {'CA': 7.25, 'NY': 4.0, 'TX': 6.25, 'FL': 6.0};
                              setSettings({ 
                                ...settings, 
                                tax_state: stateCode,
                                tax_rate: taxRates[stateCode] || 0
                              });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">Select a state</option>
                            <option value="CA">California (7.25%)</option>
                            <option value="NY">New York (4.0%)</option>
                            <option value="TX">Texas (6.25%)</option>
                            <option value="FL">Florida (6.0%)</option>
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Custom Tax Rate (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="0.00"
                            value={settings.tax_custom_rate || 0}
                            onChange={(e) => {
                              const rate = parseFloat(e.target.value) || 0;
                              setSettings({ ...settings, tax_custom_rate: rate, tax_rate: rate });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      )}

                      {/* Current Tax Rate Display */}
                      {settings.tax_rate > 0 && (
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Current Tax Rate</p>
                          <p className="text-xl font-bold text-gray-900">{settings.tax_rate}%</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Space Modal */}
        {showSpaceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: '90vh' }}>
              {/* Sticky Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">
                  {editingSpace ? 'Edit Space' : 'Add New Space'}
                </h3>
                <button
                  onClick={() => {
                    setShowSpaceModal(false);
                    setSpaceForm({ 
                      space_name: '', 
                      description: '',
                      category_id: '',
                      schedule_duration_months: 1,
                      is_24_hours: false,
                      start_time: '09:00',
                      end_time: '17:00',
                      slot_duration_minutes: 30,
                      enable_slot_booking: false,
                      bulk_start_date: '',
                      bulk_end_date: ''
                    });
                    setEditingSpace(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Space Name *</label>
                  <input
                    type="text"
                    value={spaceForm.space_name}
                    onChange={(e) => setSpaceForm({ ...spaceForm, space_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="e.g., 20 Sqft, Deluxe Suite"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={spaceForm.description}
                    onChange={(e) => setSpaceForm({ ...spaceForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    rows={2}
                    placeholder="Optional description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={spaceForm.category_id}
                    onChange={(e) => setSpaceForm({ ...spaceForm, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">No Category</option>
                    {spaceCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Assign a category to help organize and filter spaces
                  </p>
                </div>

                {/* Component Display Toggle */}
                <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <span className="text-base">📦</span>
                        Enable Component Display
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Show this space in CMS Listing components
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={spaceForm.component_display_enabled}
                        onChange={(e) => setSpaceForm({ ...spaceForm, component_display_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={spaceForm.component_display_enabled ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    {editingSpace ? 'Update Schedule Configuration' : 'Schedule Configuration'}
                  </h4>
                  
                  {!editingSpace && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Duration *</label>
                      <select
                        value={spaceForm.schedule_duration_months}
                        onChange={(e) => setSpaceForm({ ...spaceForm, schedule_duration_months: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="1">1 Month</option>
                        <option value="2">2 Months</option>
                        <option value="3">3 Months</option>
                        <option value="4">4 Months</option>
                        <option value="5">5 Months</option>
                        <option value="6">6 Months</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically create schedules for all days
                      </p>
                    </div>
                  )}

                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h5 className="text-sm font-semibold text-blue-900 mb-3">
                      {editingSpace ? 'Bulk Update Availability' : 'Bulk Schedule/Block Dates (Optional)'}
                    </h5>
                    <p className="text-xs text-blue-700 mb-3">
                      {editingSpace 
                        ? 'Select a date range. Click "Schedule" to make dates available for booking, or "Block" to prevent bookings.'
                        : 'Select a date range. Click "Schedule" to make dates available for booking, or "Block" to keep them unavailable.'}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
                        <input
                          type="date"
                          value={spaceForm.bulk_start_date || ''}
                          onChange={(e) => setSpaceForm({ ...spaceForm, bulk_start_date: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Date *</label>
                        <input
                          type="date"
                          value={spaceForm.bulk_end_date || ''}
                          onChange={(e) => setSpaceForm({ ...spaceForm, bulk_end_date: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                          min={spaceForm.bulk_start_date || new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleBulkCalendarUpdate('schedule')}
                        disabled={!spaceForm.bulk_start_date || !spaceForm.bulk_end_date}
                        className={`${editingSpace ? 'flex-1' : 'w-full'} px-3 py-2 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                      >
                        ✓ Schedule Dates (Available)
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBulkCalendarUpdate('block')}
                        disabled={!spaceForm.bulk_start_date || !spaceForm.bulk_end_date}
                        className={`${editingSpace ? 'flex-1' : 'w-full'} px-3 py-2 text-xs font-medium text-red-700 bg-red-100 border border-red-300 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                      >
                        🚫 Block Dates (Unavailable)
                      </button>
                    </div>
                    
                    {editingSpace && (
                      <p className="text-xs text-gray-600 mt-2">
                        Note: Individual day schedules can still be edited directly in the calendar below.
                      </p>
                    )}
                    
                    {!editingSpace && tempBlockedDates.length > 0 && (
                      <p className="text-xs text-red-700 font-medium mt-2">
                        🚫 {tempBlockedDates.length} date(s) will be blocked (unavailable) when space is created
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={spaceForm.is_24_hours}
                        onChange={(e) => setSpaceForm({ ...spaceForm, is_24_hours: e.target.checked })}
                        className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                      />
                      <span className="ml-2 text-sm text-gray-700">Open 24 Hours</span>
                    </label>
                  </div>

                  {!spaceForm.is_24_hours && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                        <input
                          type="time"
                          value={spaceForm.start_time}
                          onChange={(e) => setSpaceForm({ ...spaceForm, start_time: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                        <input
                          type="time"
                          value={spaceForm.end_time}
                          onChange={(e) => setSpaceForm({ ...spaceForm, end_time: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={spaceForm.enable_slot_booking}
                        onChange={(e) => setSpaceForm({ ...spaceForm, enable_slot_booking: e.target.checked })}
                        className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enable Hourly/Slot Booking</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      When disabled, users can book for multiple days (overnight boarding)
                    </p>
                  </div>

                  {spaceForm.enable_slot_booking && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Slot Duration (minutes) *</label>
                      <input
                        type="number"
                        min="15"
                        step="15"
                        value={spaceForm.slot_duration_minutes}
                        onChange={(e) => setSpaceForm({ ...spaceForm, slot_duration_minutes: parseInt(e.target.value) || 30 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="30"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Duration of each booking slot (for hourly bookings)
                      </p>
                    </div>
                  )}

                  {!spaceForm.enable_slot_booking && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        ✓ Multi-day booking enabled - Users can book this space for overnight stays and extended periods
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-white">
                <button
                  onClick={() => {
                    setShowSpaceModal(false);
                    setSpaceForm({ 
                      space_name: '', 
                      description: '',
                      category_id: '',
                      schedule_duration_months: 1,
                      is_24_hours: false,
                      start_time: '09:00',
                      end_time: '17:00',
                      slot_duration_minutes: 30,
                      enable_slot_booking: false,
                      bulk_start_date: '',
                      bulk_end_date: ''
                    });
                    setEditingSpace(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSpace}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {loading ? 'Saving...' : editingSpace ? 'Update Space' : 'Add Space'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Schedule Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">
                  {editingSchedule?.id ? 'Edit Schedule' : 'Add Schedule'} - {editingSchedule?.day_of_week}
                </h3>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.start_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.end_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slot Duration (minutes) *</label>
                  <input
                    type="number"
                    min="15"
                    step="15"
                    value={scheduleForm.slot_duration_minutes}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, slot_duration_minutes: parseInt(e.target.value) || 30 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="30"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={scheduleForm.is_enabled}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, is_enabled: e.target.checked })}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <label className="ml-2 text-sm text-gray-700">Enable this schedule</label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduleForm({ start_time: '09:00', end_time: '17:00', slot_duration_minutes: 30, is_enabled: true, breaks: [] });
                    setEditingSchedule(null);
                    setSelectedSpaceId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSchedule}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {loading ? 'Saving...' : editingSchedule?.id ? 'Update Schedule' : 'Add Schedule'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Field Modal */}
        {showAddFieldModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
              {/* Sticky Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">{editingField ? 'Edit Field' : 'Add Form Field'}</h3>
                <button onClick={resetFieldForm}>
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Field Name *</label>
                    <input
                      type="text"
                      value={fieldForm.field_name}
                      onChange={(e) => setFieldForm({ ...fieldForm, field_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter field name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Field Type *</label>
                    <select
                      value={fieldForm.field_type}
                      onChange={(e) => setFieldForm({ ...fieldForm, field_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {fieldTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {(fieldForm.field_type === 'dropdown' || fieldForm.field_type === 'radio') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Options (comma-separated)</label>
                      <input
                        type="text"
                        value={fieldForm.options}
                        onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Option 1, Option 2, Option 3"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                    <input
                      type="text"
                      value={fieldForm.placeholder}
                      onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter placeholder text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={fieldForm.description}
                      onChange={(e) => setFieldForm({ ...fieldForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows={3}
                      placeholder="Optional description or help text"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={fieldForm.required}
                      onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                      className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                    />
                    <label className="ml-2 text-sm text-gray-700">Required field</label>
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={resetFieldForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddField}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {editingField ? 'Update Field' : 'Add Field'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Document Modal */}
        {showAddDocumentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">Add Upload Field</h3>
                <button onClick={() => setShowAddDocumentModal(false)}>
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={documentForm.title}
                    onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Vaccination Records"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={documentForm.description}
                    onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Optional description or instructions"
                  />
                </div>

                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
                  <Upload className="h-4 w-4" />
                  <span>Customers will see an upload button for this document in the booking form</span>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddDocumentModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddDocument}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Check className="h-4 w-4" />
                  Add Field
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Date Modal */}
      {showDateModal && editingDateEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-lg font-semibold text-white">
                Edit Date Status
              </h3>
              <button
                onClick={() => {
                  setShowDateModal(false);
                  setEditingDateEntry(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="text"
                  value={new Date(editingDateEntry.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!editingDateEntry.is_blocked}
                    onChange={(e) => {
                      setEditingDateEntry({ ...editingDateEntry, is_blocked: !e.target.checked });
                    }}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <span className="text-sm font-medium text-gray-700">On <span className="text-gray-500">(Block)</span></span>
                </label>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dateForm.is_24_hours}
                    onChange={(e) => setDateForm({ ...dateForm, is_24_hours: e.target.checked })}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <span className="text-sm font-medium text-gray-700">24 Hours Available</span>
                </label>
              </div>

              {!dateForm.is_24_hours && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={dateForm.start_time}
                      onChange={(e) => setDateForm({ ...dateForm, start_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={dateForm.end_time}
                      onChange={(e) => setDateForm({ ...dateForm, end_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}
              
              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dateForm.enable_slot_booking}
                    onChange={(e) => setDateForm({ ...dateForm, enable_slot_booking: e.target.checked })}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable Slot Booking</span>
                </label>
              </div>
              
              {dateForm.enable_slot_booking && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slot Duration (minutes)</label>
                  <input
                    type="number"
                    value={dateForm.slot_duration_minutes}
                    onChange={(e) => setDateForm({ ...dateForm, slot_duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    min="15"
                    step="15"
                  />
                </div>
              )}

              <div className="mt-6">
                <button
                  onClick={async () => {
                    try {
                      // Update schedule settings for this day of week
                      const schedule = getScheduleForDay(editingDateEntry.spaceId, editingDateEntry.dayOfWeek);
                      
                      const scheduleData = {
                        start_time: dateForm.is_24_hours ? '00:00' : dateForm.start_time,
                        end_time: dateForm.is_24_hours ? '23:59' : dateForm.end_time,
                        slot_duration_minutes: dateForm.is_24_hours ? 1440 : (dateForm.enable_slot_booking ? dateForm.slot_duration_minutes : 1440),
                        is_enabled: true
                      };
                      
                      if (schedule) {
                        // Update existing schedule
                        await axios.put(
                          `${API_BASE_URL}/api/boarding-space-schedules/${schedule.id}`,
                          scheduleData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                      } else {
                        // Create new schedule for this day of week
                        await axios.post(
                          `${API_BASE_URL}/api/boarding-space-schedules`,
                          {
                            ...scheduleData,
                            space_id: editingDateEntry.spaceId,
                            day_of_week: editingDateEntry.dayOfWeek,
                            breaks: []
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                      }
                      
                      // Update blocked/available status for this specific date
                      await axios.post(
                        `${API_BASE_URL}/api/boarding-space-blocked-dates/bulk`,
                        {
                          space_id: editingDateEntry.spaceId,
                          dates: [editingDateEntry.date],
                          is_blocked: editingDateEntry.is_blocked,
                          reason: editingDateEntry.is_blocked ? 'Blocked by admin' : 'Scheduled/Available for booking'
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      // Small delay to ensure database is updated
                      await new Promise(resolve => setTimeout(resolve, 300));
                      
                      // Refresh data before closing modal
                      await fetchSpaceSchedules();
                      await fetchBlockedDates();
                      
                      // Close modal and show success message
                      setShowDateModal(false);
                      setEditingDateEntry(null);
                      showMessage('success', 'Changes saved successfully');
                    } catch (error) {
                      console.error('Save error:', error);
                      showMessage('error', 'Failed to save changes');
                    }
                  }}
                  className="w-full px-4 py-2 text-sm font-medium bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Manage Categories Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">Manage Space Categories</h3>
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryForm({ category_name: '', description: '' });
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Add/Edit Category Form */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                      <input
                        type="text"
                        value={categoryForm.category_name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, category_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="e.g., Indoor, Outdoor, VIP Suite"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        type="text"
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveCategory}
                        disabled={loading}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {editingCategory ? 'Update Category' : 'Add Category'}
                      </button>
                      {editingCategory && (
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setCategoryForm({ category_name: '', description: '' });
                          }}
                          className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Categories List */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Existing Categories</h4>
                  {spaceCategories.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No categories yet. Add your first category above.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {spaceCategories.map(category => (
                        <div key={category.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{category.category_name}</div>
                            {category.description && (
                              <div className="text-xs text-gray-500 mt-0.5">{category.description}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingCategory(category);
                                setCategoryForm({
                                  category_name: category.category_name,
                                  description: category.description || ''
                                });
                              }}
                              className="p-1.5 text-theme-blue hover:bg-blue-50 rounded"
                              title="Edit category"
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete category"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryForm({ category_name: '', description: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Field Confirmation Modal */}
        {showDeleteFieldModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Delete Field</h3>
                    <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete the field <span className="font-semibold">&ldquo;{fieldToDelete?.fieldName}&rdquo;</span>?
                </p>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteFieldModal(false);
                    setFieldToDelete(null);
                  }}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteField}
                  className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Field
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reservation Details Modal */}
        {showReservationModal && selectedReservation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                <div>
                  <h3 className="text-base font-semibold text-white">
                    {isEditMode ? 'Edit Reservation' : 'Reservation Details'}
                  </h3>
                  <p className="text-xs text-white text-opacity-90 mt-0.5">
                    #{selectedReservation.reservation_number || selectedReservation.id?.slice(0, 8)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowReservationModal(false);
                    setSelectedReservation(null);
                    setIsEditMode(false);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin">
                {!modalDataReady ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading reservation data...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Drop-off and Pick-up */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                        <h4 className="text-xs font-semibold text-blue-700 uppercase mb-2">Drop-off</h4>
                        {isEditMode ? (
                          <div className="space-y-2">
                            <input
                              type="date"
                              value={editForm.dropoff_date || ''}
                              onChange={(e) => setEditForm({...editForm, dropoff_date: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                            <input
                              type="time"
                              value={editForm.dropoff_time || ''}
                              onChange={(e) => setEditForm({...editForm, dropoff_time: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-semibold text-blue-900">{selectedReservation.dropoff_date}</p>
                            <p className="text-sm text-blue-700">{selectedReservation.dropoff_time}</p>
                          </>
                        )}
                      </div>
                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                        <h4 className="text-xs font-semibold text-green-700 uppercase mb-2">Pick-up</h4>
                        {isEditMode ? (
                          <div className="space-y-2">
                            <input
                              type="date"
                              value={editForm.pickup_date || ''}
                              onChange={(e) => setEditForm({...editForm, pickup_date: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                            <input
                              type="time"
                              value={editForm.pickup_time || ''}
                              onChange={(e) => setEditForm({...editForm, pickup_time: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-semibold text-green-900">{selectedReservation.pickup_date}</p>
                            <p className="text-sm text-green-700">{selectedReservation.pickup_time}</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Service */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Service</h4>
                      {isEditMode ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <select
                              value={editForm.selected_service_id || ''}
                              onChange={(e) => handleServiceChange(e.target.value)}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                              <option value="">Select Service</option>
                              {modalServices.map(service => (
                                <option key={service.id} value={service.id}>
                                  {service.service_name || service.name}
                                </option>
                              ))}
                            </select>
                            
                            {/* Edit Options Button - Only show if service has options */}
                            {editForm.selected_service_id && (() => {
                              // Get the full service object from modalServices
                              const serviceFromList = modalServices.find(s => s.id === editForm.selected_service_id);
                              const currentService = editForm.selected_service;
                              
                              // Check if service has options
                              const hasOptions = (serviceFromList?.options && serviceFromList.options.length > 0) || 
                                                 (currentService?.options && currentService.options.length > 0);
                              
                              if (!hasOptions) return null;
                              
                              return (
                                <button
                                  onClick={() => {
                                    const fullService = serviceFromList || currentService;
                                    const currentOptions = editForm.selected_service.selectedOptions || {};
                                    setSelectedServiceOptions(currentOptions);
                                    setShowOptionsModal(true);
                                  }}
                                  className="px-3 py-2 text-xs font-medium text-white rounded-lg whitespace-nowrap hover:opacity-90 transition-opacity"
                                  style={{ backgroundColor: primaryColor }}
                                >
                                  Edit Options
                                </button>
                              );
                            })()}
                          </div>
                          
                          {/* Edit Mode Pricing Breakdown */}
                          {editForm.selected_service && (
                            <div className="mt-2 bg-blue-50 rounded px-3 py-2 space-y-2">
                              {editForm.dropoff_date && editForm.dropoff_time && editForm.pickup_date && editForm.pickup_time && (
                                <div className="flex items-center justify-between text-xs text-gray-600 pb-2 border-b border-blue-200">
                                  <span>Stay Duration:</span>
                                  <span className="font-semibold">
                                    {(() => {
                                      const duration = calculateStayDuration(
                                        editForm.dropoff_date,
                                        editForm.dropoff_time,
                                        editForm.pickup_date,
                                        editForm.pickup_time
                                      );
                                      const days = Math.floor(duration / 24);
                                      const hours = Math.floor(duration % 24);
                                      if (days > 0) {
                                        return hours > 0 
                                          ? `${days} day${days > 1 ? 's' : ''} ${hours} hr${hours !== 1 ? 's' : ''}`
                                          : `${days} day${days > 1 ? 's' : ''}`;
                                      }
                                      return `${duration.toFixed(1)} hours`;
                                    })()}
                                  </span>
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">
                                  {editForm.selected_service.is_variable ? 'Service (Duration-based)' : 'Service Price'}
                                </span>
                                <span className="font-bold text-gray-900">
                                  ${(() => {
                                    const duration = calculateStayDuration(
                                      editForm.dropoff_date,
                                      editForm.dropoff_time,
                                      editForm.pickup_date,
                                      editForm.pickup_time
                                    );
                                    return calculateVariantPrice(editForm.selected_service, duration).toFixed(2);
                                  })()}
                                </span>
                              </div>
                              
                              {editForm.selected_service.is_variable && editForm.selected_service.variants && (
                                <div className="pt-2 border-t border-blue-200 space-y-1">
                                  {(() => {
                                    const duration = calculateStayDuration(
                                      editForm.dropoff_date,
                                      editForm.dropoff_time,
                                      editForm.pickup_date,
                                      editForm.pickup_time
                                    );
                                    const breakdown = getVariantBreakdown(editForm.selected_service, duration);
                                    return breakdown.map((item, idx) => (
                                      <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                                        <span className="ml-2">→ {item.count}× {item.name} @ ${item.price.toFixed(2)}</span>
                                        <span className="font-medium">${(item.count * item.price).toFixed(2)}</span>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              )}
                              
                              {/* Selected Options Display */}
                              {editForm.selected_service.selectedOptions && Object.keys(editForm.selected_service.selectedOptions).length > 0 && (
                                <div className="pt-2 border-t border-blue-200 space-y-1">
                                  <div className="text-xs font-semibold text-gray-700 mb-1">Selected Options:</div>
                                  {editForm.selected_service.options?.map((option) => {
                                    const selectedValue = editForm.selected_service.selectedOptions[option.option_name];
                                    if (!selectedValue) return null;
                                    
                                    // Handle checkbox/single type
                                    if (selectedValue === true) {
                                      const optionPrice = parseFloat(option.price) || 0;
                                      return (
                                        <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                          <span className="ml-2">→ {option.option_name}</span>
                                          <span className="font-medium">
                                            {optionPrice > 0 ? `+$${optionPrice.toFixed(2)}` : 'Included'}
                                          </span>
                                        </div>
                                      );
                                    }
                                    
                                    // Handle dropdown type
                                    if (selectedValue.choice_id) {
                                      const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                      if (choice) {
                                        const choicePrice = parseFloat(choice.price) || 0;
                                        return (
                                          <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                            <span className="ml-2">→ {option.option_name}: {choice.choice_name}</span>
                                            <span className="font-medium">
                                              {choicePrice > 0 ? `+$${choicePrice.toFixed(2)}` : 'Included'}
                                            </span>
                                          </div>
                                        );
                                      }
                                    }
                                    
                                    return null;
                                  })}
                                </div>
                              )}
                              
                              {/* Total Price (Service + Options + Tax - Token) */}
                              {(() => {
                                const duration = calculateStayDuration(
                                  editForm.dropoff_date,
                                  editForm.dropoff_time,
                                  editForm.pickup_date,
                                  editForm.pickup_time
                                );
                                const servicePrice = calculateVariantPrice(editForm.selected_service, duration);
                                
                                // Calculate options total
                                let optionsTotal = 0;
                                if (editForm.selected_service.selectedOptions) {
                                  editForm.selected_service.options?.forEach((option) => {
                                    const selectedValue = editForm.selected_service.selectedOptions[option.option_name];
                                    if (selectedValue === true) {
                                      optionsTotal += parseFloat(option.price) || 0;
                                    } else if (selectedValue?.choice_id) {
                                      const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                      if (choice) {
                                        optionsTotal += parseFloat(choice.price) || 0;
                                      }
                                    }
                                  });
                                }
                                
                                const subtotal = servicePrice + optionsTotal;
                                const taxAmount = settings.tax_enabled ? (subtotal * (settings.tax_rate || 0)) / 100 : 0;
                                const grandTotal = subtotal + taxAmount;
                                const tokenPaid = parseFloat(selectedReservation?.payment_token_amount) || 0;
                                const pendingAmount = grandTotal - tokenPaid;
                                
                                return (
                                  <>
                                    {settings.tax_enabled && settings.tax_rate > 0 && (
                                      <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-gray-600">
                                        <span>Tax ({settings.tax_rate}%):</span>
                                        <span className="font-medium">${taxAmount.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {tokenPaid > 0 && (
                                      <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-green-600">
                                        <span>Token Amount Paid:</span>
                                        <span className="font-medium">-${tokenPaid.toFixed(2)}</span>
                                      </div>
                                    )}
                                    <div className="pt-2 mt-2 border-t-2 border-gray-300 flex items-center justify-between">
                                      <span className="text-sm font-bold text-gray-900">
                                        {tokenPaid > 0 ? 'Total (Pending):' : 'Total:'}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900">
                                        ${tokenPaid > 0 ? pendingAmount.toFixed(2) : grandTotal.toFixed(2)}
                                      </span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="text-sm text-gray-900 font-medium mb-2">
                            {selectedReservation.selected_service?.service_name || selectedReservation.selected_service?.name || 'No service selected'}
                          </div>
                          {selectedReservation.selected_service && (
                            <div className="bg-blue-50 rounded px-3 py-2 space-y-2">
                              {selectedReservation.dropoff_date && selectedReservation.dropoff_time && selectedReservation.pickup_date && selectedReservation.pickup_time && (
                                <div className="flex items-center justify-between text-xs text-gray-600 pb-2 border-b border-blue-200">
                                  <span>Stay Duration:</span>
                                  <span className="font-semibold">
                                    {(() => {
                                      const duration = calculateStayDuration(
                                        selectedReservation.dropoff_date,
                                        selectedReservation.dropoff_time,
                                        selectedReservation.pickup_date,
                                        selectedReservation.pickup_time
                                      );
                                      const days = Math.floor(duration / 24);
                                      const hours = Math.floor(duration % 24);
                                      if (days > 0) {
                                        return hours > 0 
                                          ? `${days} day${days > 1 ? 's' : ''} ${hours} hr${hours !== 1 ? 's' : ''}`
                                          : `${days} day${days > 1 ? 's' : ''}`;
                                      }
                                      return `${duration.toFixed(1)} hours`;
                                    })()}
                                  </span>
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">
                                  {selectedReservation.selected_service.is_variable ? 'Service (Duration-based)' : 'Service Price'}
                                </span>
                                <span className="font-bold text-gray-900">
                                  ${(() => {
                                    const duration = calculateStayDuration(
                                      selectedReservation.dropoff_date,
                                      selectedReservation.dropoff_time,
                                      selectedReservation.pickup_date,
                                      selectedReservation.pickup_time
                                    );
                                    return calculateVariantPrice(selectedReservation.selected_service, duration).toFixed(2);
                                  })()}
                                </span>
                              </div>
                              
                              {selectedReservation.selected_service.is_variable && selectedReservation.selected_service.variants && (
                                <div className="pt-2 border-t border-blue-200 space-y-1">
                                  {(() => {
                                    const duration = calculateStayDuration(
                                      selectedReservation.dropoff_date,
                                      selectedReservation.dropoff_time,
                                      selectedReservation.pickup_date,
                                      selectedReservation.pickup_time
                                    );
                                    const breakdown = getVariantBreakdown(selectedReservation.selected_service, duration);
                                    return breakdown.map((item, idx) => (
                                      <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                                        <span className="ml-2">→ {item.count}× {item.name} @ ${item.price.toFixed(2)}</span>
                                        <span className="font-medium">${(item.count * item.price).toFixed(2)}</span>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              )}
                              
                              {/* Selected Options Display */}
                              {selectedReservation.selected_service.selectedOptions && Object.keys(selectedReservation.selected_service.selectedOptions).length > 0 && (
                                <div className="pt-2 border-t border-blue-200 space-y-1">
                                  <div className="text-xs font-semibold text-gray-700 mb-1">Selected Options:</div>
                                  {selectedReservation.selected_service.options?.map((option) => {
                                    const selectedValue = selectedReservation.selected_service.selectedOptions[option.option_name];
                                    if (!selectedValue) return null;
                                    
                                    // Handle checkbox/single type
                                    if (selectedValue === true) {
                                      const optionPrice = parseFloat(option.price) || 0;
                                      return (
                                        <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                          <span className="ml-2">→ {option.option_name}</span>
                                          <span className="font-medium">
                                            {optionPrice > 0 ? `+$${optionPrice.toFixed(2)}` : 'Included'}
                                          </span>
                                        </div>
                                      );
                                    }
                                    
                                    // Handle dropdown type
                                    if (selectedValue.choice_id) {
                                      const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                      if (choice) {
                                        const choicePrice = parseFloat(choice.price) || 0;
                                        return (
                                          <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                            <span className="ml-2">→ {option.option_name}: {choice.choice_name}</span>
                                            <span className="font-medium">
                                              {choicePrice > 0 ? `+$${choicePrice.toFixed(2)}` : 'Included'}
                                            </span>
                                          </div>
                                        );
                                      }
                                    }
                                    
                                    return null;
                                  })}
                                </div>
                              )}
                              
                              {/* Total Price (Service + Options + Tax - Token) */}
                              {(() => {
                                const duration = calculateStayDuration(
                                  selectedReservation.dropoff_date,
                                  selectedReservation.dropoff_time,
                                  selectedReservation.pickup_date,
                                  selectedReservation.pickup_time
                                );
                                const servicePrice = calculateVariantPrice(selectedReservation.selected_service, duration);
                                
                                // Calculate options total
                                let optionsTotal = 0;
                                if (selectedReservation.selected_service.selectedOptions) {
                                  selectedReservation.selected_service.options?.forEach((option) => {
                                    const selectedValue = selectedReservation.selected_service.selectedOptions[option.option_name];
                                    if (selectedValue === true) {
                                      optionsTotal += parseFloat(option.price) || 0;
                                    } else if (selectedValue?.choice_id) {
                                      const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                      if (choice) {
                                        optionsTotal += parseFloat(choice.price) || 0;
                                      }
                                    }
                                  });
                                }
                                
                                const subtotal = servicePrice + optionsTotal;
                                const taxAmount = settings.tax_enabled ? (subtotal * (settings.tax_rate || 0)) / 100 : 0;
                                const grandTotal = subtotal + taxAmount;
                                const tokenPaid = parseFloat(selectedReservation?.payment_token_amount) || 0;
                                const pendingAmount = grandTotal - tokenPaid;
                                
                                return (
                                  <>
                                    {settings.tax_enabled && settings.tax_rate > 0 && (
                                      <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-gray-600">
                                        <span>Tax ({settings.tax_rate}%):</span>
                                        <span className="font-medium">${taxAmount.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {tokenPaid > 0 && (
                                      <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-green-600">
                                        <span>Token Amount Paid:</span>
                                        <span className="font-medium">-${tokenPaid.toFixed(2)}</span>
                                      </div>
                                    )}
                                    <div className="pt-2 mt-2 border-t-2 border-gray-300 flex items-center justify-between">
                                      <span className="text-sm font-bold text-gray-900">
                                        {tokenPaid > 0 ? 'Total (Pending):' : 'Total:'}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900">
                                        ${tokenPaid > 0 ? pendingAmount.toFixed(2) : grandTotal.toFixed(2)}
                                      </span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Space */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Assigned Space</h4>
                      {isEditMode ? (
                        <select
                          value={editForm.assigned_space_id || ''}
                          onChange={(e) => setEditForm({...editForm, assigned_space_id: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        >
                          <option value="">Select Space</option>
                          {filteredSpaces.map(space => (
                            <option key={space.id} value={space.id}>
                              {space.space_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm font-medium text-gray-900">
                          {selectedReservation.assigned_space_name}
                        </p>
                      )}
                    </div>

                    {/* Customer & Form Details */}
                    {selectedReservation.form_details && Object.keys(selectedReservation.form_details).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(selectedReservation.form_details)
                            .map(([key, value]) => {
                              const fieldName = getFieldNameById(key, 'form');
                              return { key, value, fieldName };
                            })
                            .filter(({ fieldName }) => {
                              // Filter out fields with excessive underscores or special patterns
                              const fieldNameLower = fieldName.toLowerCase();
                              const hasExcessiveUnderscores = (fieldName.match(/_/g) || []).length > 2;
                              const hasSpecialPattern = fieldNameLower.includes('_for_') || fieldNameLower.includes('_-_') || fieldNameLower.includes('default_');
                              // Check if it looks like a UUID
                              const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fieldName);
                              
                              return !hasExcessiveUnderscores && !hasSpecialPattern && !isUuid;
                            })
                            .map(({ key, value, fieldName }) => (
                              <div key={key}>
                                <label className="text-xs font-medium text-gray-500">{fieldName}</label>
                                <p className="text-sm text-gray-900 mt-0.5">{value || 'N/A'}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Room Details */}
                    {selectedReservation.room_details && Object.keys(selectedReservation.room_details).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Pet/Room Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(selectedReservation.room_details)
                            .map(([key, value]) => {
                              const fieldName = getFieldNameById(key, 'room');
                              return { key, value, fieldName };
                            })
                            .filter(({ fieldName }) => {
                              // Filter out ugly fields
                              const fieldNameLower = fieldName.toLowerCase();
                              const hasExcessiveUnderscores = (fieldName.match(/_/g) || []).length > 2;
                              const hasSpecialPattern = fieldNameLower.includes('_for_') || fieldNameLower.includes('_-_') || fieldNameLower.includes('default_');
                              const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fieldName);
                              
                              return !hasExcessiveUnderscores && !hasSpecialPattern && !isUuid;
                            })
                            .map(({ key, value, fieldName }) => (
                              <div key={key}>
                                <label className="text-xs font-medium text-gray-500">{fieldName}</label>
                                <p className="text-sm text-gray-900 mt-0.5">
                                  {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value || 'N/A')}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Special Instructions */}
                    {selectedReservation.special_instructions && Object.keys(selectedReservation.special_instructions).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Special Instructions</h4>
                        <div className="space-y-2">
                          {Object.entries(selectedReservation.special_instructions)
                            .map(([key, value]) => {
                              const fieldName = getFieldNameById(key, 'special');
                              return { key, value, fieldName };
                            })
                            .filter(({ fieldName }) => {
                              // Filter out ugly fields
                              const fieldNameLower = fieldName.toLowerCase();
                              const hasExcessiveUnderscores = (fieldName.match(/_/g) || []).length > 2;
                              const hasSpecialPattern = fieldNameLower.includes('_for_') || fieldNameLower.includes('_-_') || fieldNameLower.includes('default_');
                              const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fieldName);
                              
                              return !hasExcessiveUnderscores && !hasSpecialPattern && !isUuid;
                            })
                            .map(({ key, value, fieldName }) => (
                              <div key={key}>
                                <label className="text-xs font-medium text-gray-500">{fieldName}</label>
                                <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{value || 'N/A'}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-2 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
                {isEditMode ? (
                  <>
                    {/* Status Dropdown - Left Side */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-gray-600">Status:</label>
                      <select
                        value={editForm.status || ''}
                        onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="confirmed">Confirmed</option>
                        <option value="checked_in">Checked In</option>
                        <option value="checked_out">Checked Out</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    
                    {/* Buttons - Right Side */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveReservationEdit}
                        className="px-4 py-1.5 text-sm text-white rounded-md font-medium transition-colors flex items-center gap-1.5"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Save className="h-3.5 w-3.5" />
                        Update Reservation
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Status Display - Left Side */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-600">Status:</span>
                      <span className="px-3 py-1 text-sm font-semibold text-gray-900 capitalize bg-gray-100 rounded-lg">
                        {selectedReservation.status}
                      </span>
                    </div>
                    
                    {/* Buttons - Right Side */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="px-3 py-1.5 text-sm text-white rounded-md font-medium transition-colors flex items-center gap-1.5"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Compact Loading Modal */}
      {isUpdating && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg px-6 py-4 flex items-center gap-3 shadow-xl">
            {/* Animated Circular Spinner */}
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-theme-blue border-t-transparent"></div>
            {/* Loading Text */}
            <p className="text-sm font-medium text-gray-700">Updating reservation...</p>
          </div>
        </div>
      )}

      {/* Custom Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 flex items-center gap-3">
              <div className="bg-white bg-opacity-20 rounded-full p-2">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">No Space Available</h3>
            </div>
            
            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-gray-700 text-sm leading-relaxed">
                {errorMessage}
              </p>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end">
              <button
                onClick={handleErrorModalClose}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors shadow-sm"
                style={{ backgroundColor: primaryColor }}
              >
                OK, Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Options Modal */}
      {showOptionsModal && editForm.selected_service && (() => {
        // Get the service with full options - check modalServices if editForm doesn't have them
        const serviceWithOptions = editForm.selected_service.options && editForm.selected_service.options.length > 0
          ? editForm.selected_service
          : modalServices.find(s => s.id === editForm.selected_service_id) || editForm.selected_service;
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: `${primaryColor}10` }}>
              <h3 className="text-lg font-bold text-gray-900">
                Service Options: {serviceWithOptions.service_name || serviceWithOptions.name}
              </h3>
              <button
                onClick={() => setShowOptionsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="px-6 py-4 overflow-y-auto flex-1 scrollbar-thin">
              <p className="text-sm text-gray-600 mb-4">
                Review and select options for your service. Previously selected options have been carried over where available.
              </p>
              
              <div className="space-y-4">
                {serviceWithOptions.options?.map((option) => (
                  <div key={option.option_id} className="border border-gray-200 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-900 mb-2 block">
                      {option.option_name}
                      {option.is_required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {option.option_type === 'single' || option.option_type === 'checkbox' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedServiceOptions[option.option_name] === true}
                          onChange={(e) => handleOptionChange(option.option_name, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                          style={{ accentColor: primaryColor }}
                        />
                        <span className="text-sm text-gray-700">
                          {option.price > 0 && `+$${option.price}`}
                        </span>
                      </div>
                    ) : option.option_type === 'dropdown' ? (
                      <select
                        value={selectedServiceOptions[option.option_name]?.choice_id || ''}
                        onChange={(e) => {
                          const choice = option.choices.find(c => c.choice_id === e.target.value);
                          handleOptionChange(option.option_name, choice ? { choice_id: choice.choice_id, choice_name: choice.choice_name } : null);
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        <option value="">Select...</option>
                        {option.choices?.map((choice) => (
                          <option key={choice.choice_id} value={choice.choice_id}>
                            {choice.choice_name} {choice.price > 0 && `(+$${choice.price})`}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowOptionsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Skip Options
              </button>
              <button
                onClick={handleOptionsConfirm}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg"
                style={{ backgroundColor: primaryColor }}
              >
                Confirm Options
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Delete All Bookings Confirmation Modal */}
      {showDeleteAllBookingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete All Bookings?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete <strong>ALL boarding bookings</strong>? This will free up all calendar spaces but the action <strong>cannot be undone</strong>.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteAllBookingsModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllBookings}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All Bookings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Tour Schedules Confirmation Modal */}
      {showDeleteAllToursModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete All Tour Schedules?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete <strong>ALL tour schedules</strong>? This action <strong>cannot be undone</strong>.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteAllToursModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllTours}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All Tour Schedules
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBoardingCalendar;